<template>
    <b-container fluid>
        <h1 class="text-left">
            {{ title }}
            <em
                id="theme_unavailable"
                v-show="theme != null && theme.unavailable"
                title="thema niet beschikbaar"
                class="error fas fa-exclamation-triangle"
            >
            </em>
        </h1>
        <br />
        <b-badge variant="primary" v-show="loading">{{
            $t('loading')
        }}</b-badge>
        <b-form
            ref="themeform"
            class="col-xs-8 col-md-12 extra-padding-bottom"
            v-if="!loading"
            name="themeform"
            v-on:submit.prevent="$_onSubmit"
        >
            <!-- Name -->
            <div>
                <x-input
                    :id="`id_${theme.id}`"
                    :name="`name_${theme.id}`"
                    :text="$t('columnNameName')"
                    maxlength="50"
                    :state="!errorInUse"
                    v-model="theme.name"
                    @input="$_exists"
                    :invalidMessage="$t('themeInUse')"
                    required
                    autofocus
                >
                </x-input>
                <!-- Description -->
                <div class="form-group">
                    <label :for="`${theme.id}description`">{{
                        $t('description')
                    }}</label>
                    <x-textarea
                        :id="`description${theme.id}`"
                        :name="`description${theme.id}`"
                        :rows="3"
                        :maxlength="2000"
                        v-model="theme.description"
                        required
                    >
                    </x-textarea>
                </div>
                <div>
                    <div class="form-group">
                        <div class="d-flex">
                            <x-checkbox
                                id="Covering"
                                name="Covering"
                                v-model="theme.covering"
                                :text="$t('labelThemeCovering')"
                            >
                            </x-checkbox>
                            <info-button class="pl-1" name="coveringTooltip" />
                            <tooltip
                                target="coveringTooltip"
                                :title="$t('helpThemeCovering')"
                                :customClass="'xlarge'"
                                :timeOut="tooltipTimeOut"
                            >
                            </tooltip>
                        </div>
                    </div>
                </div>
                <!-- Themegroup -->
                <div class="form-group">
                    <x-select
                        :id="`themeGroup${theme.id}`"
                        :name="`themeGroup${theme.id}`"
                        :value="theme.themeGroupId"
                        :text="`${$t('labelThemeGroup')}${$t('labelOptional')}`"
                        @selected="$_themeGroupSelected($event)"
                        :options="
                            $_convertToValueTextPair(
                                this.themeGroupOptions,
                                'id',
                                'name'
                            )
                        "
                        :simpleMode="true"
                    >
                    </x-select>
                </div>
            </div>
            <!--Themelayers-->
            <div class="mb-1">{{ $t('labelThemelayers') }}</div>
            <v-draggable :list="theme.themeLayers" handle=".fa-arrows-alt">
                <div
                    role="tablist"
                    v-for="(themelayer, index) in theme.themeLayers"
                    :key="index"
                >
                    <b-card no-body class="mb-1">
                        <b-card-header
                            :id="`headerLayer${themelayer.id}`"
                            v-b-toggle="`accordion${themelayer.id}`"
                            header-tag="header"
                            class="p-1"
                            role="tab"
                            data-toggle="collapse"
                        >
                            <em class="fa fa-arrows-alt"></em>
                            <span>{{ themelayer.title }}</span>
                            <span class="float-right">
                                <!--  Show crossed out eye when layer not active -->
                                <img
                                    v-if="
                                        !themelayer.active ||
                                        !themelayer.activeInTheme
                                    "
                                    class="not-clickable theme-eye px-1"
                                    src="~@/assets/imported-icons/ic_fluent_eye_hide_20_regular.svg"
                                    alt="theme not visible icon"
                                    :title="$_notActiveLabel(themelayer.active)"
                                />
                                <!-- Delete icon in header -->
                                <a
                                    :id="`${themelayer.id}removeLayer`"
                                    :title="$t('themelayerDelete')"
                                    class="pr-4"
                                    @click.stop.prevent="$_removeLayer(index)"
                                >
                                    <em class="fas fa-trash-alt fa-sm"></em>
                                </a>
                                <!-- Expand/collapse icon -->
                                <em class="fa iconCollapseExpand"></em>
                            </span>
                        </b-card-header>
                        <b-collapse
                            v-bind:id="`accordion${themelayer.id}`"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body class="reduced-padding-top">
                                <b-container class="themelayer-container">
                                    <b-row>
                                        <b-col>
                                            <!-- Alternative title-->
                                            <x-input
                                                :id="`alternativetitle${themelayer.id}`"
                                                :name="`alternativetitle${themelayer.id}`"
                                                :text="
                                                    $t('labelAlternativeTitle')
                                                "
                                                maxlength="50"
                                                v-model="
                                                    themelayer.alternativeTitle
                                                "
                                            >
                                            </x-input>
                                            <!--Active-->
                                            <x-checkbox
                                                :disabled="!themelayer.active"
                                                :id="`active${themelayer.id}`"
                                                :name="`active${themelayer.id}`"
                                                extendedClass="checkbox-padding-bottom"
                                                v-model="
                                                    themelayer.activeInTheme
                                                "
                                                :text="
                                                    $_activeInThemeLabel(
                                                        themelayer.active
                                                    )
                                                "
                                            >
                                            </x-checkbox>
                                            <!--Visible-->
                                            <x-checkbox
                                                :id="`visible${themelayer.id}`"
                                                :name="`visible${themelayer.id}`"
                                                extendedClass="checkbox-padding-bottom"
                                                v-model="themelayer.visible"
                                                :text="`${$t(
                                                    'labelVisible'
                                                )} (${$t('webOnly')})`"
                                            >
                                            </x-checkbox>
                                            <!--LegendExpanded-->
                                            <x-checkbox
                                                :id="`legendExpanded${themelayer.id}`"
                                                :name="`legendExpanded${themelayer.id}`"
                                                extendedClass="checkbox-padding-bottom"
                                                v-model="
                                                    themelayer.legendExpanded
                                                "
                                                :text="`${$t(
                                                    'labelLegendExpanded'
                                                )} (${$t('webOnly')})`"
                                            >
                                            </x-checkbox>
                                            <!--Exportable-->
                                            <x-checkbox
                                                v-if="
                                                    $_hasWfsServiceType(
                                                        themelayer
                                                    )
                                                "
                                                :id="`exportable${themelayer.id}`"
                                                :name="`exportable${themelayer.id}`"
                                                extendedClass="checkbox-padding-bottom"
                                                v-model="themelayer.exportable"
                                                :text="$t('labelExportable')"
                                            >
                                            </x-checkbox>
                                            <!--Print-->
                                            <x-checkbox
                                                v-if="
                                                    $_hasWfsServiceType(
                                                        themelayer
                                                    )
                                                "
                                                :id="`print${themelayer.id}`"
                                                :name="`print${themelayer.id}`"
                                                extendedClass="checkbox-padding-bottom"
                                                v-model="themelayer.print"
                                                :text="$t('labelPrint')"
                                            >
                                            </x-checkbox>
                                            <!-- Legend sort order -->
                                            <x-number
                                                extendedClass="number-padding-bottom"
                                                v-model="themelayer.sortOrder"
                                                required
                                                :minValue="0"
                                                :maxValue="999"
                                                :step="1"
                                                :text="$t('labelSortOrder')"
                                            >
                                            </x-number>
                                            <!-- WFS-T edit rights -->
                                            <b-row
                                                v-if="$_isWfsTLayer(themelayer)"
                                                class="ml-1"
                                            >
                                                <b-form-group>
                                                    <template v-slot:label>
                                                        <span>
                                                            {{
                                                                $t(
                                                                    'labelWfsTSetMutateOptions'
                                                                )
                                                            }}
                                                        </span>
                                                    </template>
                                                    <b-form-checkbox-group
                                                        v-model="
                                                            themelayer.mutateOptionsWfsTArray
                                                        "
                                                        @input="
                                                            $_setMutateOptionsWfsT(
                                                                themelayer,
                                                                $event
                                                            )
                                                        "
                                                    >
                                                        <b-form-checkbox
                                                            name="`chkUpdate${themelayer.id}`"
                                                            value="U"
                                                        >
                                                            {{
                                                                $t(
                                                                    'labelUpdate'
                                                                )
                                                            }}
                                                        </b-form-checkbox>
                                                    </b-form-checkbox-group>
                                                </b-form-group>
                                            </b-row>
                                        </b-col>
                                        <b-col
                                            v-if="
                                                $_hasWfsServiceType(themelayer)
                                            "
                                        >
                                            <!-- GeometryType -->
                                            <b-form-group>
                                                <template v-slot:label>
                                                    <span>
                                                        {{ $t('geomType') }}
                                                    </span>
                                                    <template
                                                        v-if="
                                                            themelayer.geometryTypeIsFromLayer
                                                        "
                                                    >
                                                        <info-button
                                                            class="pl-1"
                                                            :name="`geometryTypeTooltip${themelayer.id}`"
                                                        />
                                                        <tooltip
                                                            :target="`geometryTypeTooltip${themelayer.id}`"
                                                            :title="
                                                                $t(
                                                                    'nedglobeServices.geometryTypeTooltip'
                                                                )
                                                            "
                                                            :customClass="'medium'"
                                                            :timeOut="
                                                                tooltipTimeOut
                                                            "
                                                        >
                                                        </tooltip>
                                                    </template>
                                                </template>
                                                <b-form-checkbox-group
                                                    v-model="
                                                        themelayer.geometryTypeArray
                                                    "
                                                    @input="
                                                        $_setGeometryType(
                                                            themelayer,
                                                            $event
                                                        )
                                                    "
                                                    :disabled="
                                                        themelayer.geometryTypeIsFromLayer
                                                    "
                                                >
                                                    <b-form-checkbox
                                                        name="chkGeoTypePoint"
                                                        value="P"
                                                    >
                                                        {{ $t('geomPoint') }}
                                                    </b-form-checkbox>
                                                    <b-form-checkbox
                                                        name="chkGeoTypeLine"
                                                        value="L"
                                                    >
                                                        {{ $t('geomLine') }}
                                                    </b-form-checkbox>
                                                    <b-form-checkbox
                                                        name="chkGeoTypePolygon"
                                                        value="V"
                                                    >
                                                        {{ $t('geomPolygon') }}
                                                    </b-form-checkbox>
                                                </b-form-checkbox-group>
                                            </b-form-group>
                                        </b-col>
                                        <b-col
                                            v-if="
                                                $_hasPointGeometry(themelayer)
                                            "
                                        >
                                            <!-- RotationAttribute -->
                                            <label
                                                :for="`rotationAttribute${themelayer.id}`"
                                            >
                                                {{ $t('rotationAttribute') }}
                                            </label>
                                            <x-select
                                                :id="`rotationAttribute${themelayer.id}`"
                                                :name="`rotationAttribute${themelayer.id}`"
                                                v-model="
                                                    themelayer.rotationAttribute
                                                "
                                                :options="
                                                    $_convertToValueTextPair(
                                                        themelayer.informationAttributes,
                                                        'attribute',
                                                        'attribute'
                                                    )
                                                "
                                                :simpleMode="true"
                                            >
                                            </x-select>
                                        </b-col>
                                    </b-row>
                                    <b-row
                                        v-if="themelayer.queryable"
                                        class="mt-2"
                                    >
                                        <b-col>
                                            <!-- ShowFeatureInfo-->
                                            <x-checkbox
                                                :id="`showFeatureInfo${themelayer.id}`"
                                                :name="`showFeatureInfo${themelayer.id}`"
                                                extendedClass="checkbox-padding-bottom"
                                                :enableClickEvent="true"
                                                v-model="
                                                    themelayer.showFeatureInfo
                                                "
                                                :text="`${$t(
                                                    'labelShowFeatureInfo'
                                                )}`"
                                            >
                                            </x-checkbox>
                                        </b-col>
                                    </b-row>
                                    <template
                                        v-if="
                                            themelayer.queryable &&
                                            themelayer.showFeatureInfo
                                        "
                                    >
                                        <b-row class="mb-3">
                                            <b-col>
                                                <!-- featureinfoX -->
                                                <label
                                                    :for="`featureInfoX${themelayer.id}`"
                                                    >{{ $t('coordX') }}</label
                                                >
                                                <b-form-input
                                                    :id="`featureInfoX${themelayer.id}`"
                                                    :name="`featureInfoX${themelayer.id}`"
                                                    type="number"
                                                    step="0.001"
                                                    extendedClass="number-padding-bottom"
                                                    v-model="
                                                        themelayer.featureInfoX
                                                    "
                                                    @paste.stop.prevent="
                                                        $_onpaste(
                                                            $event,
                                                            themelayer,
                                                            'x'
                                                        )
                                                    "
                                                >
                                                </b-form-input>
                                            </b-col>
                                            <b-col>
                                                <!-- featureinfoX -->
                                                <label
                                                    :for="`featureInfoY${themelayer.id}`"
                                                    >{{ $t('coordY') }}</label
                                                >
                                                <b-form-input
                                                    :id="`featureInfoY${themelayer.id}`"
                                                    :name="`featureInfoY${themelayer.id}`"
                                                    type="number"
                                                    step="0.001"
                                                    extendedClass="number-padding-bottom"
                                                    v-model="
                                                        themelayer.featureInfoY
                                                    "
                                                    :text="$t('coordY')"
                                                    @paste.stop.prevent="
                                                        $_onpaste(
                                                            $event,
                                                            themelayer,
                                                            'y'
                                                        )
                                                    "
                                                >
                                                </b-form-input
                                            ></b-col>
                                        </b-row>
                                        <b-row class="mb-3">
                                            <b-col>
                                                <b-button
                                                    id="addAttributes"
                                                    @click="
                                                        $_addAttributes(
                                                            themelayer
                                                        )
                                                    "
                                                    variant="secondary"
                                                    >{{
                                                        $t(
                                                            'labelShowGetFeatureInfo'
                                                        )
                                                    }}
                                                </b-button>
                                            </b-col>
                                        </b-row>
                                    </template>
                                </b-container>
                                <!--Tabs-->
                                <b-tabs
                                    content-class="mt-3"
                                    active-nav-item-class="text-primary"
                                >
                                    <!--Informatieweergave-->
                                    <b-tab
                                        v-if="
                                            $_hasWfsServiceType(themelayer) ||
                                            $_hasWmsAttributes(themelayer)
                                        "
                                        :title="
                                            $t('labelTabInformationDisplay')
                                        "
                                        active
                                    >
                                        <information-tab
                                            id="themeInformationTab"
                                            ref="informationTab"
                                            :themelayer="themelayer"
                                            @update="
                                                $_updateInformationAttributes(
                                                    $event,
                                                    themelayer
                                                )
                                            "
                                        >
                                        </information-tab>
                                    </b-tab>
                                    <!--Stijlen-->
                                    <b-tab :title="$t('labelTabStyle')">
                                        <div
                                            role="tablist"
                                            v-for="(
                                                style, sindex
                                            ) in themelayer.styles"
                                            :key="sindex"
                                        >
                                            <b-card no-body class="mb-1">
                                                <b-card-header
                                                    :id="`headerStyle${themelayer.id}`"
                                                    v-b-toggle="
                                                        `accordionThemeStyle{style.id}`
                                                    "
                                                    header-tag="header"
                                                    class="p-1"
                                                    role="tab"
                                                    data-toggle="collapse"
                                                >
                                                    <span
                                                        >Stijl ({{
                                                            style.minScale
                                                        }}
                                                        -
                                                        {{
                                                            style.maxScale
                                                        }})</span
                                                    >
                                                </b-card-header>
                                                <b-collapse
                                                    v-bind:id="`accordionThemeStyle{style.id}`"
                                                    accordion="my-styleaccordion"
                                                    role="tabpanel"
                                                    visible
                                                >
                                                    <b-card-body>
                                                        <div
                                                            role="group"
                                                            class="form-row style-max-width"
                                                        >
                                                            <label
                                                                for="`slMinScale_${style.id}`"
                                                                class="col-form-label font-weight-normal style-label-md2"
                                                            >
                                                                {{
                                                                    $t(
                                                                        'scaleRange'
                                                                    )
                                                                }}
                                                            </label>
                                                            <label
                                                                for="`slMinScale_${style.id}`"
                                                                class="col-form-label font-weight-normal"
                                                                >min</label
                                                            >
                                                            <div class="col">
                                                                <x-select
                                                                    :id="`slMinScale_${style.id}`"
                                                                    :name="`slMinScale_${style.id}`"
                                                                    v-model="
                                                                        style.minScale
                                                                    "
                                                                    @selected="
                                                                        $_onSelectMinScale(
                                                                            $event,
                                                                            style
                                                                        )
                                                                    "
                                                                    :options="
                                                                        scaleOptions
                                                                    "
                                                                    :simpleMode="
                                                                        true
                                                                    "
                                                                    :showDefaultOption="
                                                                        false
                                                                    "
                                                                >
                                                                </x-select>
                                                            </div>
                                                            <label
                                                                for="`slMaxScale_${style.id}`"
                                                                class="col-form-label font-weight-normal"
                                                                >max</label
                                                            >
                                                            <div class="col">
                                                                <x-select
                                                                    :id="`slMaxScale_${style.id}`"
                                                                    :name="`slMaxScale_${style.id}`"
                                                                    v-model="
                                                                        style.maxScale
                                                                    "
                                                                    @selected="
                                                                        $_onSelectMaxScale(
                                                                            $event,
                                                                            style
                                                                        )
                                                                    "
                                                                    :options="
                                                                        scaleOptions
                                                                    "
                                                                    :simpleMode="
                                                                        true
                                                                    "
                                                                    :showDefaultOption="
                                                                        false
                                                                    "
                                                                >
                                                                </x-select>
                                                            </div>
                                                        </div>
                                                        <div
                                                            role="group"
                                                            class="form-row style-max-width"
                                                        >
                                                            <label
                                                                :for="`slTextMaxScale_${style.id}`"
                                                                class="col-form-label font-weight-normal style-label-md2"
                                                            >
                                                                {{
                                                                    $t(
                                                                        'labelTextMaxScale'
                                                                    )
                                                                }}
                                                            </label>
                                                            <label
                                                                for="`slTextMaxScale_${style.id}`"
                                                                class="col-form-label font-weight-normal"
                                                                >max</label
                                                            >
                                                            <div class="col">
                                                                <x-select
                                                                    :id="`slTextMaxScale_${style.id}`"
                                                                    :name="`slTextMaxScale_${style.id}`"
                                                                    v-model="
                                                                        style.textMaxScale
                                                                    "
                                                                    class="w-50"
                                                                    @selected="
                                                                        $_onSelectTextMaxScale(
                                                                            $event,
                                                                            style
                                                                        )
                                                                    "
                                                                    :options="
                                                                        textScaleOptions(
                                                                            style.minScale,
                                                                            style.maxScale
                                                                        )
                                                                    "
                                                                    :simpleMode="
                                                                        true
                                                                    "
                                                                    :showDefaultOption="
                                                                        true
                                                                    "
                                                                >
                                                                </x-select>
                                                            </div>
                                                        </div>
                                                        <div
                                                            role="group"
                                                            class="form-row style-max-width"
                                                        >
                                                            <label
                                                                :for="`${style.id}Transparency`"
                                                                class="col-form-label font-weight-normal style-label-md2"
                                                            >
                                                                {{
                                                                    `${$t(
                                                                        'layerSetOpacityTitle'
                                                                    )} %`
                                                                }}
                                                            </label>
                                                            <div class="col">
                                                                <x-number
                                                                    class="transparency-input"
                                                                    v-model="
                                                                        style.transparency
                                                                    "
                                                                    required
                                                                    :minValue="
                                                                        0
                                                                    "
                                                                    :maxValue="
                                                                        100
                                                                    "
                                                                    :step="1"
                                                                    :maxLength="
                                                                        3
                                                                    "
                                                                ></x-number>
                                                            </div>
                                                        </div>
                                                        <div
                                                            role="group"
                                                            class="form-row style-max-width"
                                                            v-if="
                                                                $_showGrafischeLaag(
                                                                    themelayer
                                                                )
                                                            "
                                                        >
                                                            <label
                                                                :for="`wmsStyles${style.id}`"
                                                                class="col-form-label font-weight-normal style-label-md2"
                                                            >
                                                                {{
                                                                    $_getStyleLable(
                                                                        themelayer
                                                                    )
                                                                }}
                                                            </label>
                                                            <div class="col">
                                                                <x-select
                                                                    :id="`wmsStyles${style.id}`"
                                                                    :name="`wmsStyles${style.id}`"
                                                                    v-model="
                                                                        style.name
                                                                    "
                                                                    @selected="
                                                                        $_onSelectWmsStyleName(
                                                                            $event,
                                                                            style
                                                                        )
                                                                    "
                                                                    :options="
                                                                        $_graphicalLayerOptions(
                                                                            themelayer
                                                                        )
                                                                    "
                                                                    :simpleMode="
                                                                        true
                                                                    "
                                                                >
                                                                </x-select>
                                                            </div>
                                                        </div>
                                                        <b-tabs
                                                            content-class="mt-3"
                                                        >
                                                            <!--Tab Standaard-->
                                                            <b-tab
                                                                :title="
                                                                    $t(
                                                                        'tabTitleStandard'
                                                                    )
                                                                "
                                                                v-if="
                                                                    $_hasWfsServiceType(
                                                                        themelayer
                                                                    )
                                                                "
                                                            >
                                                                <default-style-tab
                                                                    :selectedStyle="
                                                                        style
                                                                    "
                                                                    :textAttributeOptions="
                                                                        themelayer.informationAttributes
                                                                    "
                                                                    name="`ThStStandard_${style.id}`"
                                                                    :inputFields="
                                                                        themelayer.informationAttributes
                                                                    "
                                                                    :domains="
                                                                        domains
                                                                    "
                                                                    @reevaluatestyle="
                                                                        $_onReevaluateStyle
                                                                    "
                                                                    :geometryTypeArray="
                                                                        themelayer.geometryTypeArray
                                                                    "
                                                                    :isWfsWithGraphicalLayer="
                                                                        $_isWfsWithGraphicalLayer(
                                                                            themelayer
                                                                        )
                                                                    "
                                                                >
                                                                </default-style-tab>
                                                            </b-tab>
                                                            <!--Tab Selectie-->
                                                            <b-tab
                                                                :title="
                                                                    $t(
                                                                        'tabTitleSelection'
                                                                    )
                                                                "
                                                                v-if="
                                                                    $_hasWfsServiceType(
                                                                        themelayer
                                                                    )
                                                                "
                                                            >
                                                                <style-tab
                                                                    :themeStyle="
                                                                        style
                                                                    "
                                                                    :textAttributeOptions="
                                                                        themelayer.informationAttributes
                                                                    "
                                                                    tabSelection="Selection"
                                                                    :name="`ThStSelection_${style.id}`"
                                                                    :geometryTypeArray="
                                                                        themelayer.geometryTypeArray
                                                                    "
                                                                    :showSaveDefaultButton="
                                                                        true
                                                                    "
                                                                >
                                                                </style-tab>
                                                            </b-tab>
                                                            <!--Tab Detail-->
                                                            <b-tab
                                                                :title="
                                                                    $t(
                                                                        'tabTitleDetail'
                                                                    )
                                                                "
                                                                v-if="
                                                                    $_hasWfsServiceType(
                                                                        themelayer
                                                                    )
                                                                "
                                                            >
                                                                <style-tab
                                                                    :themeStyle="
                                                                        style
                                                                    "
                                                                    :textAttributeOptions="
                                                                        themelayer.informationAttributes
                                                                    "
                                                                    tabSelection="Selected"
                                                                    :name="`ThStDetails_${style.id}`"
                                                                    :geometryTypeArray="
                                                                        themelayer.geometryTypeArray
                                                                    "
                                                                    :showSaveDefaultButton="
                                                                        true
                                                                    "
                                                                >
                                                                </style-tab>
                                                            </b-tab>
                                                        </b-tabs>
                                                    </b-card-body>
                                                </b-collapse>
                                            </b-card>
                                        </div>
                                    </b-tab>
                                    <!--Filterattributen-->
                                    <b-tab
                                        v-if="$_hasWfsServiceType(themelayer)"
                                        :title="$t('labelFilterAttributes')"
                                    >
                                        <v-draggable
                                            :list="themelayer.filters"
                                            handle=".fa-arrows-alt"
                                        >
                                            <div
                                                role="tablist"
                                                v-for="(
                                                    filterattr, findex
                                                ) in themelayer.filters"
                                                :key="findex"
                                            >
                                                <b-card no-body class="mb-1">
                                                    <b-card-header
                                                        :id="`headerFilterAttr${themelayer.id}`"
                                                        v-b-toggle="
                                                            `accordionFilterAttr${filterattr.id}`
                                                        "
                                                        header-tag="header"
                                                        class="p-1"
                                                        role="tab"
                                                        data-toggle="collapse"
                                                    >
                                                        <em
                                                            class="fa fa-arrows-alt"
                                                        ></em>
                                                        <span>{{
                                                            filterattr.name
                                                        }}</span>
                                                        <span
                                                            class="float-right"
                                                        >
                                                            <!-- Delete icon in header -->
                                                            <a
                                                                :id="`${filterattr.id}removeFilter`"
                                                                :name="`${filterattr.id}removeFilter`"
                                                                :title="
                                                                    $t(
                                                                        'filterAttributeRemove'
                                                                    )
                                                                "
                                                                class="pr-4"
                                                                @click.stop.prevent="
                                                                    $_removeFilter(
                                                                        themelayer.filters,
                                                                        findex
                                                                    )
                                                                "
                                                            >
                                                                <em
                                                                    class="fas fa-trash-alt fa-sm"
                                                                ></em>
                                                            </a>
                                                            <!-- Expand/collapse icon -->
                                                            <em
                                                                class="fa iconCollapseExpand"
                                                            ></em>
                                                        </span>
                                                    </b-card-header>
                                                    <b-collapse
                                                        v-bind:id="`accordionFilterAttr${filterattr.id}`"
                                                        accordion="my-filteraccordion"
                                                        role="tabpanel"
                                                        @show="
                                                            getDomainKeyValues(
                                                                filterattr
                                                            )
                                                        "
                                                        visible
                                                    >
                                                        <b-card-body>
                                                            <div
                                                                class="col-sm-7 col-md-9"
                                                            >
                                                                <!-- groot scherm / klein scherm  -->
                                                                <div>
                                                                    <!-- Attribuut-->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterAttribuut`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                {{
                                                                                    $t(
                                                                                        'labelAttributeName'
                                                                                    )
                                                                                }}
                                                                            </label>
                                                                            <x-select
                                                                                :id="`filterAttribuut${filterattr.id}`"
                                                                                :name="`filterAttribuut${filterattr.id}`"
                                                                                v-model="
                                                                                    filterattr.filterAttribute
                                                                                "
                                                                                @changed="
                                                                                    $_filterAttributeSelected(
                                                                                        $event,
                                                                                        filterattr,
                                                                                        themelayer
                                                                                    )
                                                                                "
                                                                                :options="
                                                                                    $_filterAttributeOptions(
                                                                                        themelayer.informationAttributes
                                                                                    )
                                                                                "
                                                                                :simpleMode="
                                                                                    true
                                                                                "
                                                                                :invalidMessage="
                                                                                    filterattr.attributeErrorMessage
                                                                                "
                                                                                :state="
                                                                                    filterattr
                                                                                        .attributeErrorMessage
                                                                                        .length ===
                                                                                    0
                                                                                "
                                                                            >
                                                                            </x-select>
                                                                            <!-- Warning -->
                                                                            <em
                                                                                v-show="
                                                                                    filterattr.unavailable
                                                                                "
                                                                                class="error fas fa-exclamation-triangle"
                                                                                title="Filter niet beschikbaar, stel het filter attribuut opnieuw in."
                                                                            ></em>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Filter naam -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`filterNaam${filterattr.id}`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                {{
                                                                                    $t(
                                                                                        'columnNameName'
                                                                                    )
                                                                                }}
                                                                            </label>
                                                                            <x-input
                                                                                :id="`filterId_${filterattr.id}`"
                                                                                :name="`filterNaam${filterattr.id}`"
                                                                                maxlength="50"
                                                                                :state="
                                                                                    filterattr
                                                                                        .errorMessage
                                                                                        .length ===
                                                                                    0
                                                                                "
                                                                                @change="
                                                                                    $_filterNameValidate(
                                                                                        filterattr,
                                                                                        themelayer
                                                                                    )
                                                                                "
                                                                                v-model="
                                                                                    filterattr.name
                                                                                "
                                                                                :invalidMessage="
                                                                                    filterNameErrorMessage
                                                                                "
                                                                                :placeholder="
                                                                                    placeholders.filterName
                                                                                "
                                                                                :justInvalidState="
                                                                                    true
                                                                                "
                                                                            >
                                                                            </x-input>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Operator-->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-if="
                                                                            filterattr.filterAttribute !==
                                                                            null
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterOperators`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                {{
                                                                                    filterattr.operatorEditable
                                                                                        ? $t(
                                                                                              'labelOperatorDefault'
                                                                                          )
                                                                                        : $t(
                                                                                              'labelOperator'
                                                                                          )
                                                                                }}
                                                                            </label>
                                                                            <x-select
                                                                                :id="`filterOperators${filterattr.id}`"
                                                                                :name="`filterOperators${filterattr.id}`"
                                                                                v-model="
                                                                                    filterattr.operator
                                                                                "
                                                                                @selected="
                                                                                    $_onSelectFilterOperatorOption(
                                                                                        $event,
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :options="
                                                                                    $_filterOperatorOptions(
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :simpleMode="
                                                                                    true
                                                                                "
                                                                                :invalidMessage="
                                                                                    filterattr.operatorErrorMessage
                                                                                "
                                                                                :state="
                                                                                    filterattr
                                                                                        .operatorErrorMessage
                                                                                        .length ===
                                                                                    0
                                                                                "
                                                                                :disabled="
                                                                                    filterattr.unavailable
                                                                                "
                                                                            >
                                                                            </x-select>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Operator zichtbaar-->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-if="
                                                                            filterattr.filterAttribute !==
                                                                            null
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}OperatorEditable`"
                                                                                class="custom style-label-md"
                                                                                >{{
                                                                                    $t(
                                                                                        'labelOperatorVisible'
                                                                                    )
                                                                                }}</label
                                                                            >
                                                                            <x-checkbox
                                                                                :id="`${filterattr.id}OperatorEditable`"
                                                                                :name="`${filterattr.id}OperatorEditable`"
                                                                                :disabled="
                                                                                    !filterattr.visible ||
                                                                                    filterattr.domainType ===
                                                                                        domainTypeConst.ALL_VALUES
                                                                                "
                                                                                v-model="
                                                                                    filterattr.operatorEditable
                                                                                "
                                                                            >
                                                                            </x-checkbox>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Invoer-->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-if="
                                                                            filterattr.filterAttribute !==
                                                                            null
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterWaardeinvoer`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                {{
                                                                                    $t(
                                                                                        'labelInput'
                                                                                    )
                                                                                }}
                                                                            </label>
                                                                            <x-select
                                                                                :id="`filterWaardeinvoer${filterattr.id}`"
                                                                                :name="`filterWaardeinvoer${filterattr.id}`"
                                                                                v-model="
                                                                                    filterattr.valueInput
                                                                                "
                                                                                @selected="
                                                                                    $_valueInputSelected(
                                                                                        $event,
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :options="
                                                                                    $_filterInputTypeOptions(
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :simpleMode="
                                                                                    true
                                                                                "
                                                                                :invalidMessage="
                                                                                    filterattr.valueInputErrorMessage
                                                                                "
                                                                                :state="
                                                                                    filterattr
                                                                                        .valueInputErrorMessage
                                                                                        .length ===
                                                                                    0
                                                                                "
                                                                                :disabled="
                                                                                    filterattr.unavailable
                                                                                "
                                                                            >
                                                                            </x-select>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Domeinwaarden -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-if="
                                                                            filterattr.valueInput ===
                                                                            valueInputTypeConst.DOMAIN
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterDomainSelect`"
                                                                                class="custom style-label-md"
                                                                                >{{
                                                                                    $t(
                                                                                        'labelDomain'
                                                                                    )
                                                                                }}</label
                                                                            >
                                                                            <x-select
                                                                                :id="`filterDomainSelect${filterattr.id}`"
                                                                                :name="`filterDomainSelect${filterattr.id}`"
                                                                                v-model="
                                                                                    filterattr.domainId
                                                                                "
                                                                                @selected="
                                                                                    $_filterDomainSelected(
                                                                                        $event,
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :options="
                                                                                    $_filterDomainOptions()
                                                                                "
                                                                                :state="
                                                                                    filterattr
                                                                                        .domainErrorMessage
                                                                                        .length ===
                                                                                    0
                                                                                "
                                                                                :invalidMessage="
                                                                                    filterattr.domainErrorMessage
                                                                                "
                                                                                :simpleMode="
                                                                                    true
                                                                                "
                                                                            >
                                                                            </x-select>
                                                                            <b-link
                                                                                :id="`${filterattr.id}addDomain`"
                                                                                :name="`${filterattr.id}addDomain`"
                                                                                v-show="
                                                                                    filterattr.valueInput ===
                                                                                    valueInputTypeConst.DOMAIN
                                                                                "
                                                                                @click="
                                                                                    $_openDomainModal(
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                            >
                                                                                &nbsp;
                                                                                <em
                                                                                    class="fa fa-plus"
                                                                                ></em
                                                                                >&nbsp;{{
                                                                                    $t(
                                                                                        'domainAddButton'
                                                                                    )
                                                                                }}
                                                                            </b-link>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Domein Selectie Type  -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-if="
                                                                            filterattr.valueInput ===
                                                                                valueInputTypeConst.DOMAIN &&
                                                                            filterattr.domainId
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterDomainType`"
                                                                                class="custom style-label-md"
                                                                                >{{
                                                                                    $t(
                                                                                        'domainShowThrough'
                                                                                    )
                                                                                }}</label
                                                                            >
                                                                            <x-select
                                                                                :id="`filterDomainType${filterattr.id}`"
                                                                                :name="`filterDomainType${filterattr.id}`"
                                                                                style="
                                                                                    vertical-align: top;
                                                                                "
                                                                                v-model="
                                                                                    filterattr.domainType
                                                                                "
                                                                                @selected="
                                                                                    $_domainTypeSelected(
                                                                                        $event,
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :options="
                                                                                    $_convertToValueTextPair(
                                                                                        allDomainTypes,
                                                                                        'code',
                                                                                        'description'
                                                                                    )
                                                                                "
                                                                                :simpleMode="
                                                                                    true
                                                                                "
                                                                                :state="
                                                                                    filterattr
                                                                                        .domainTypeErrorMessage
                                                                                        .length ===
                                                                                    0
                                                                                "
                                                                                :invalidMessage="
                                                                                    filterattr.domainTypeErrorMessage
                                                                                "
                                                                                :disabled="
                                                                                    filterattr.unavailable
                                                                                "
                                                                            >
                                                                            </x-select>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Waarde (Domein of Tekst) -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-if="
                                                                            filterattr.domainType !==
                                                                            domainTypeConst.ALL_VALUES
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group info-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterValue`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                {{
                                                                                    `${$t(
                                                                                        'labelValue'
                                                                                    )}${$t(
                                                                                        'labelOptional'
                                                                                    )}`
                                                                                }}
                                                                                <!-- Info for SYSDATE value -->
                                                                                <span
                                                                                    v-show="
                                                                                        $_classifyFilterDataType(
                                                                                            filterattr.dataType
                                                                                        ) ===
                                                                                            'datetime' &&
                                                                                        filterattr.valueInput !==
                                                                                            valueInputTypeConst.DOMAIN
                                                                                    "
                                                                                    class="pr-1"
                                                                                >
                                                                                    <info-button
                                                                                        :name="`${filterattr.id}filterValueInfo`"
                                                                                    />
                                                                                    <tooltip
                                                                                        :target="`${filterattr.id}filterValueInfo`"
                                                                                        :title="
                                                                                            $t(
                                                                                                'filterSysdateInputInfo'
                                                                                            )
                                                                                        "
                                                                                        :customClass="'medium'"
                                                                                    >
                                                                                    </tooltip>
                                                                                </span>
                                                                            </label>
                                                                            <!-- Waarde voor Domein -->
                                                                            <x-select
                                                                                :id="`filterValue${filterattr.id}`"
                                                                                :name="`filterValue${filterattr.id}`"
                                                                                ref="domainSelect"
                                                                                v-if="
                                                                                    filterattr.valueInput ===
                                                                                    valueInputTypeConst.DOMAIN
                                                                                "
                                                                                v-model="
                                                                                    filterattr.value
                                                                                "
                                                                                @selected="
                                                                                    $_onSelectDomainValue(
                                                                                        $event,
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :options="
                                                                                    $_domainTypeValueOptions(
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                                :simpleMode="
                                                                                    true
                                                                                "
                                                                                :disabled="
                                                                                    filterattr.unavailable
                                                                                "
                                                                            >
                                                                            </x-select>
                                                                            <!-- Waarde voor Tekst of datum invoerveld -->
                                                                            <b-form-group
                                                                                :invalid-feedback="
                                                                                    invalidDateMessageFunction
                                                                                "
                                                                                :state="
                                                                                    invalidDateMessageState
                                                                                "
                                                                                :label-for="`${filterattr.id}filterValueInput`"
                                                                            >
                                                                                <base-input
                                                                                    :id="`${filterattr.id}filterValueInput`"
                                                                                    type="text"
                                                                                    v-show="
                                                                                        filterattr.valueInput !==
                                                                                        valueInputTypeConst.DOMAIN
                                                                                    "
                                                                                    :maxlength="
                                                                                        $_getMaxInputChars(
                                                                                            filterattr
                                                                                        )
                                                                                    "
                                                                                    :disabled="
                                                                                        filterattr.unavailable
                                                                                    "
                                                                                    @blur="
                                                                                        $_filterValidate(
                                                                                            filterattr
                                                                                        )
                                                                                    "
                                                                                    v-model="
                                                                                        filterattr.value
                                                                                    "
                                                                                    @change="
                                                                                        $_onChangeInputValue(
                                                                                            $event,
                                                                                            filterattr
                                                                                        )
                                                                                    "
                                                                                    :placeholder="
                                                                                        filterattr.valueInput ===
                                                                                        valueInputTypeConst.DATEFIELD
                                                                                            ? placeholders.filterDateValue
                                                                                            : placeholders.filterTextValue
                                                                                    "
                                                                                >
                                                                                </base-input>
                                                                            </b-form-group>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Hoofdlettergevoelig -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                        v-show="
                                                                            $_isDataTypeAlphaNumeric(
                                                                                filterattr
                                                                            ) &&
                                                                            filterattr.valueInput !==
                                                                                valueInputTypeConst.DOMAIN
                                                                        "
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterCaseSens`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                {{
                                                                                    $t(
                                                                                        'caseSensitive'
                                                                                    )
                                                                                }}
                                                                            </label>
                                                                            <x-checkbox
                                                                                :id="`${filterattr.id}filterCaseSens`"
                                                                                :name="`${filterattr.id}filterCaseSens`"
                                                                                v-model="
                                                                                    filterattr.caseSensitive
                                                                                "
                                                                            >
                                                                            </x-checkbox>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Standaard geselecteerd -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                    >
                                                                        <div
                                                                            class="form-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterSelected`"
                                                                                class="custom style-label-md"
                                                                                >Standaard
                                                                                geselecteerd</label
                                                                            >
                                                                            <x-checkbox
                                                                                :id="`${filterattr.id}filterSelected`"
                                                                                :name="`${filterattr.id}filterSelected`"
                                                                                :disabled="
                                                                                    !filterattr.visible
                                                                                "
                                                                                v-model="
                                                                                    filterattr.selected
                                                                                "
                                                                            >
                                                                            </x-checkbox>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Zichtbaar in Mobile/Web -->
                                                                    <div
                                                                        class="form-inline style-item"
                                                                    >
                                                                        <div
                                                                            class="form-group info-group"
                                                                        >
                                                                            <label
                                                                                :for="`${filterattr.id}filterVisible`"
                                                                                class="custom style-label-md"
                                                                            >
                                                                                Zichtbaar
                                                                            </label>
                                                                            <x-checkbox
                                                                                :id="`${filterattr.id}filterVisible`"
                                                                                :name="`${filterattr.id}filterVisible`"
                                                                                v-model="
                                                                                    filterattr.visible
                                                                                "
                                                                                @input="
                                                                                    $_filterUpdateSelected(
                                                                                        filterattr
                                                                                    )
                                                                                "
                                                                            >
                                                                            </x-checkbox>
                                                                        </div>
                                                                    </div>
                                                                    <!-- Filter unavailable warning -->
                                                                    <div
                                                                        v-show="
                                                                            filterattr.unavailable
                                                                        "
                                                                    >
                                                                        <span
                                                                            class="danger"
                                                                        >
                                                                            Het
                                                                            filter-attribuut
                                                                            <em>
                                                                                {{
                                                                                    filterattr.filterAttribute
                                                                                }}
                                                                            </em>
                                                                            is
                                                                            niet
                                                                            beschikbaar,
                                                                            stel
                                                                            het
                                                                            filter
                                                                            attribuut
                                                                            opnieuw
                                                                            in.
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </b-card-body>
                                                    </b-collapse>
                                                </b-card>
                                            </div>
                                            <b-link
                                                :id="`${themelayer.id}addFilterButton`"
                                                :name="`${themelayer.id}addFilterButton`"
                                                @click="
                                                    $_addFilter(
                                                        themelayer,
                                                        $_getGeneratedId()
                                                    )
                                                "
                                            >
                                                <em class="fa fa-plus"></em
                                                >&nbsp;{{
                                                    $t('filterAttributeAdd')
                                                }}
                                            </b-link>
                                        </v-draggable>
                                    </b-tab>
                                    <b-tab
                                        v-if="$_hasWfsServiceType(themelayer)"
                                        :title="$t('labelAdvancedLabels')"
                                    >
                                        <advanced-labels-tab
                                            id="advancedLabelsTab"
                                            ref="advancedLabelTab"
                                            :themelayer="themelayer"
                                        >
                                        </advanced-labels-tab>
                                    </b-tab>
                                </b-tabs>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </v-draggable>
            <b-badge variant="primary" v-show="isAddingNewLayer">
                {{ $t('loading') }}
            </b-badge>
            <b-link
                :id="`addthemelayer`"
                :name="`addthemelayer`"
                v-show="!addLayerMode"
                @click="$_loadThemelayers()"
            >
                <em class="fa fa-plus"></em>&nbsp;{{
                    $t('themelayerDetailAddTitle')
                }}
            </b-link>
            <b-card no-body v-if="addLayerMode">
                <b-card-body>
                    <div>
                        <b-form-group
                            :label="$t('labelThemelayer')"
                            label-for="tags-with-dropdown"
                        >
                            <b-form-tags
                                id="tags-with-dropdown"
                                v-model="newThemeLayers"
                                no-outer-focus
                                class="mb-2"
                            >
                                <template
                                    v-slot="{
                                        tags,
                                        disabled,
                                        addTag,
                                        removeTag
                                    }"
                                >
                                    <ul
                                        v-if="tags.length > 0"
                                        class="list-inline d-inline-block mb-2"
                                    >
                                        <li
                                            v-for="tag in tags"
                                            :key="tag"
                                            class="list-inline-item"
                                        >
                                            <b-form-tag
                                                @remove="removeTag(tag)"
                                                :title="tag"
                                                :disabled="disabled"
                                                variant="info"
                                                >{{ tag }}</b-form-tag
                                            >
                                        </li>
                                    </ul>
                                    <b-dropdown
                                        size="sm"
                                        variant="outline-secondary"
                                        block
                                        menu-class="limited-heigt w-100 pt-0"
                                        @shown="$_setFocusSearchLayer"
                                    >
                                        <template #button-content>
                                            {{ $t('labelChooseThemelayers') }}
                                        </template>
                                        <b-dropdown-form
                                            @submit.stop.prevent="() => {}"
                                            class="fixed"
                                        >
                                            <b-form-group
                                                :label="$t('btnSearchLabel')"
                                                label-for="tag-search-input"
                                                label-cols-md="auto"
                                                class="mb-0"
                                                label-size="sm"
                                                :description="searchDesc"
                                                :disabled="disabled"
                                            >
                                                <b-form-input
                                                    v-model="searchVal"
                                                    id="tag-search-input"
                                                    type="search"
                                                    size="sm"
                                                    autocomplete="off"
                                                    ref="searchLayer"
                                                ></b-form-input>
                                            </b-form-group>
                                        </b-dropdown-form>
                                        <b-dropdown-item-button
                                            v-for="option in availableOptions"
                                            :key="option"
                                            @click="
                                                $_onOptionClick({
                                                    option,
                                                    addTag
                                                })
                                            "
                                        >
                                            {{ option }}
                                        </b-dropdown-item-button>
                                    </b-dropdown>
                                </template>
                            </b-form-tags>
                        </b-form-group>
                    </div>
                    <!-- Theme add / cancel buttons -->
                    <div class="btn-group">
                        <b-button
                            :id="`addLayer`"
                            @click="$_addLayer"
                            variant="primary"
                            :disabled="newThemeLayers.length === 0"
                        >
                            <span class="fa fa-check" /> {{ $t('btnAddLabel') }}
                        </b-button>
                        <b-button
                            :id="`cancelAddLayer`"
                            @click="$_cancelAddLayer"
                        >
                            <span class="fa fa-times" />
                            {{ $t('btnCancelLabel') }}
                        </b-button>
                    </div>
                </b-card-body>
            </b-card>
            <SubmitCancelRemove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="mode"
                @cancel="$_onCancel"
                @remove="$_triggerRemove"
                :isBtnSaveDisabled="isBtnSaveDisabled"
            >
            </SubmitCancelRemove>
        </b-form>
        <add-domain-modal
            :launchModal="launchModal"
            @modal-generated-domain="$_onModalGeneratedDomain"
            @modal-domain-saved="$_onModalDomainSaved"
        >
        </add-domain-modal>
    </b-container>
</template>

<script>
    import Vue from 'vue';
    import { mapActions } from 'vuex';
    import { orderBy, debounce } from 'lodash';
    import {
        arraySortAscendingFunction,
        stringNotEmpty,
        arrayNotEmpty,
        valueToName,
        convertToValueTextPair,
        nprogress,
        getRefCodes,
        filterOperatorOptions,
        getListPossibleOperators,
        classifyDataType,
        isInputDomainAndTypeAllValues
    } from '../../services/utilities';
    import {
        validateFilterNameUnique,
        validateDate
    } from '../../services/utilities/validator';
    import {
        DOMAIN_TYPE,
        LAYER_STYLE,
        MODE_ADD,
        MODE_EDIT,
        OPERATOR_TYPE,
        VALUE_INPUT_TYPE,
        OWS_TYPE,
        STYLE_CLASSIFICATION_TYPES
    } from '../../constants';
    import { cleanData } from '../../services/api/patch';
    import xinput from '../../components/BaseComponents/x-input.vue';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import styleTab from '../../components/dashboard/StylingOptions/StyleTab.vue';
    import defaultStyleTab from '../../components/dashboard/StylingOptions/DefaultStyleTab.vue';
    import AddDomainModal from '../../components/AddDomainModal.vue';
    import draggable from 'vuedraggable';
    import InfoButton from '../../components/InfoButton.vue';
    import Tooltip from '../../components/Tooltip.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XTextarea from '../../components/BaseComponents/x-textarea.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import BaseInput from '../../components/BaseComponents/base-input.vue';
    import InformationTab from '../../components/dashboard/ViewRelatedComponents/InformationTab.vue';
    import AdvancedLabelsTab from '../../components/dashboard/ViewRelatedComponents/AdvancedLabelsTab.vue';
    import XNumber from '../../components/BaseComponents/x-number.vue';
    import { getOgcStyleOptions } from '../../services/map/layer/ogcapi';

    export default {
        mixins: [modalMixin],

        components: {
            'x-input': xinput,
            'v-draggable': draggable,
            'style-tab': styleTab,
            'default-style-tab': defaultStyleTab,
            'add-domain-modal': AddDomainModal,
            'info-button': InfoButton,
            tooltip: Tooltip,
            'x-select': XSelect,
            'x-textarea': XTextarea,
            'x-checkbox': XCheckbox,
            'x-number': XNumber,
            'base-input': BaseInput,
            'information-tab': InformationTab,
            'advanced-labels-tab': AdvancedLabelsTab,
            SubmitCancelRemove
        },

        props: { id: { type: Number, required: false, default: -1 } },

        data() {
            return {
                // Constants
                valueInputTypeConst: VALUE_INPUT_TYPE,
                domainTypeConst: DOMAIN_TYPE,

                showselect: false,
                domainKeyValues: null,
                generatedId: 0,
                newLayerIndex: null,
                title: '',
                themeErrorMessagesList: [],
                filterNameErrorMessage: '',
                loading: false,
                mode: 0,
                errorInUse: null,
                isSaving: false,
                isDeleting: false,
                isAddingNewLayer: false,
                invalidFilters: false,
                addLayerMode: false,
                launchModal: false,
                allOperators: [],
                allValueInputTypes: [],
                allDomainTypes: [],
                domainTypeOptions: [],
                themelayerOptions: [],
                themeGroupOptions: [],
                theme: {},
                domains: [],
                availableScaleItems: null,
                allThemelayers: null,
                newThemeLayers: [],
                themeLayerFilter: '',
                searchVal: '',
                activeFilter: null,
                savedFilter: null,
                selectedThemeGroup: null,
                placeholders: {
                    filterName: 'Filter naam',
                    filterTextValue: 'Filter waarde',
                    filterDateValue: 'dd-mm-jjjj OF <SYSDATE>'
                },
                layerStyleConst: LAYER_STYLE,
                waarde: [],
                maxNumberOfErrors: 3,
                invalidDateMessage: '',
                tooltipTimeOut: 10000
            };
        },

        computed: {
            selectedDomain() {
                if (this.activeFilter !== null) {
                    return this.activeFilter.domainId;
                }
                return [];
            },
            /**
             * Check if theme can be saved
             * @returns {boolean} indicating if theme can be saved
             */
            isBtnSaveDisabled() {
                return this.errorInUse || this.isSaving || this.isDeleting;
            },
            scaleOptions() {
                if (this.theme.scales !== null) {
                    return convertToValueTextPair(
                        this.theme.scales,
                        'scale',
                        'scale'
                    );
                } else if (this.availableScaleItems !== null) {
                    return convertToValueTextPair(
                        this.availableScaleItems,
                        'scale',
                        'scale'
                    );
                }
                return [];
            },
            textScaleOptions() {
                return (minScale, maxScale) => {
                    return this.scaleOptions.filter(
                        ({ value }) => value >= minScale && value <= maxScale
                    );
                };
            },
            strokeWidthOptions() {
                return convertToValueTextPair(this.layerStyleConst.stroke_width);
            },
            pointRadiusOptions() {
                return convertToValueTextPair(this.layerStyleConst.point_radius);
            },
            inEditMode() {
                return this.mode === MODE_EDIT;
            },
            invalidDateMessageFunction() {
                return this.invalidDateMessage;
            },
            invalidDateMessageState() {
                return this.invalidDateMessage === '';
            },

            criteria() {
                // Compute the search criteria
                return this.searchVal.trim().toLowerCase();
            },
            availableOptions() {
                const criteria = this.criteria;
                // Filter out already selected options
                const options = this.selectableLayers.filter(
                    (opt) => this.newThemeLayers.indexOf(opt) === -1
                );
                if (criteria) {
                    // Show only options that match criteria
                    return options.filter(
                        (opt) => opt.toLowerCase().indexOf(criteria) > -1
                    );
                }
                // Show all options available
                return options;
            },
            searchDesc() {
                if (this.criteria && this.availableOptions.length === 0) {
                    return this.$t('errorSearchCriteriaNoMatch');
                }
                return '';
            },
            selectableLayers: function () {
                let options;
                if (this.theme.themeLayers && this.theme.themeLayers.length > 0) {
                    options = this.$_themelayerOptions().filter(
                        (opt) =>
                            this.theme.themeLayers.findIndex(
                                ({ title }) => title === opt.text
                            ) === -1
                    );
                } else {
                    options = this.$_themelayerOptions();
                }

                return options.map((option) => {
                    return option.text;
                });
            }
        },

        async beforeCreate() {
            // This object should be present before Theme object
            const result = await this.$store.dispatch('useApiData', {
                name: 'GetThemeScaleItems'
            });
            this.availableScaleItems = Object.assign([], result.data);
        },

        created() {
            if (this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('themeDetailEditTitle');
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('themeDetailAddTitle');
            }

            // preload SVG file list for use in SVG based styles
            this.$_setAvailableSvgFiles();
        },

        mounted() {
            this.$_init();
        },

        methods: {
            ...mapActions(['getOrganizationSetting']),
            $_init() {
                this.$_getTheme();
                this.$_initFilterAttributeData();
                this.$_loadThemeGroups();
            },
            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            },
            async $_setAvailableSvgFiles() {
                await this.$store.dispatch('setAvailableSvgFiles');
            },
            /**
             * Adds an empty filter to a themelayer
             * @param {object} themelayer the themelayer that gets a new filter
             * @param {number} generatedId generated number for unique id's in new filters for Selenium selection
             */
            $_addFilter(themelayer, generatedId) {
                const temporaryId = themelayer.filters.length + generatedId;
                // nieuw filterattribuut
                let newFilter = {
                    id: temporaryId,
                    name: null,
                    filterAttribute: null,
                    operator: null,
                    possibleOperators: null,
                    errorMessage: '',
                    attributeErrorMessage: '',
                    operatorErrorMessage: '',
                    valueInputErrorMessage: '',
                    domainErrorMessage: '',
                    domainTypeErrorMessage: '',
                    // initieel geen domain waarde
                    valueInputDomain: false,
                    valueInput: null,
                    value: null,
                    selected: false,
                    visible: true,
                    domainType: null,
                    caseSensitive: true,
                    // toevoegen als laatste in de lijst
                    order: themelayer.filters ? themelayer.filters.length + 1 : 0,
                    seleniumId: `generated${generatedId}` // prepend string, so there cannot be a duplicate with an existing id
                };
                Vue.set(themelayer.filters, themelayer.filters.length, newFilter);
            },
            // pasting coord x,y in X-field or Y-field fills both fields
            // pasting of just x or y works too now
            $_onpaste(event, themeLayer, xOrY) {
                let error = false;
                const coords = event.clipboardData.getData('text').split(' ');

                // If we have exactly 1 coordinate put in in the current field.
                if (coords.length === 1) {
                    const value = parseFloat(coords[0].replace(',', '.'));
                    if (isNaN(value)) {
                        error = true;
                    } else if (xOrY === 'x') {
                        themeLayer.featureInfoX = value;
                    } else {
                        themeLayer.featureInfoY = value;
                    }
                } else if (coords.length === 2) {
                    const x = parseFloat(coords[0].replace(',', '.'));
                    const y = parseFloat(coords[1].replace(',', '.'));

                    if (isNaN(x) || isNaN(y)) {
                        error = true;
                    } else {
                        themeLayer.featureInfoX = x;
                        themeLayer.featureInfoY = y;
                    }
                } else {
                    error = true;
                }

                if (error) {
                    const data = {
                        message: this.$t('errorInvalidCoordinatesPastedText'),
                        variant: 'warning',
                        autoHideDelay: 5000,
                        title: this.$t('errorInvalidCoordinatesPastedTitle')
                    };
                    this.showToast(data);
                }
            },

            async $_addAttributes(themeLayer) {
                // Get WMS data format
                const format = await this.$store.dispatch('useApiData', {
                    name: 'GetLayerWmsOutputFormat',
                    parameters: { Id: themeLayer.id }
                });

                // WMS GetFeatureInfo request
                const data = {
                    themeLayerId: themeLayer.id,
                    coordinates: [themeLayer.featureInfoX, themeLayer.featureInfoY],
                    format: format.data
                };

                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFeatureInfoByLayerId',
                    data,
                    useloader: true
                });

                // If there are features process the attributes
                if (result?.data?.features.length > 0) {
                    const properties = result.data.features[0].properties;

                    // The attribute names are the object keys.
                    const attributes = Object.keys(properties);
                    this.$_processAttributes(themeLayer, attributes);
                } else {
                    // Info to user that there are no attributes at this location
                    const data = {
                        message: this.$t('infoNoAttributesFoundMessage'),
                        variant: 'info',
                        title: this.$t('infoNoAttributesFoundTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            $_processAttributes(themeLayer, attributes) {
                // parameters:
                // themelayer is the layer with the informationattributes
                // attributes: Attributes found at location

                // Attributes in attributes and not in informationattributes
                // these must be added
                let addAttributes = [];
                let delAttributes = [];
                if (themeLayer.informationAttributes) {
                    addAttributes = attributes.filter(
                        (a) =>
                            !themeLayer.informationAttributes.find(
                                (ia) => ia.attribute === a
                            )
                    );
                    // Attributes in informationattributes but not in attributes
                    // these must be deleted
                    delAttributes = themeLayer.informationAttributes.filter(
                        (ia) => !attributes.includes(ia.attribute)
                    );
                } else {
                    addAttributes = attributes;
                }

                addAttributes.forEach((attribute) =>
                    this.$_addInformationAttribute(themeLayer, attribute)
                );

                delAttributes.forEach((informationAttribute) =>
                    this.$_delInformationAttribute(
                        themeLayer,
                        informationAttribute.attribute
                    )
                );

                // Message nothing has changed
                if (addAttributes.length === 0 && delAttributes.length === 0) {
                    const data = {
                        message: this.$t('infoNoNewAttributesFoundMessage'),
                        variant: 'info',
                        title: this.$t('infoNoNewAttributesFoundTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                } else {
                    // Message x new and y deleted
                    const data = {
                        message: this.$t('infoNewAttributesFoundMessage', [
                            addAttributes.length,
                            delAttributes.length
                        ]),
                        variant: 'info',
                        title: this.$t('infoNewAttributesFoundTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            $_delInformationAttribute(themeLayer, attribute) {
                // find index of item that must be deleted
                const index = themeLayer.informationAttributes.findIndex(
                    (ia) => ia.attribute === attribute
                );

                // delete by index
                if (index > -1) {
                    themeLayer.informationAttributes.splice(index, 1);
                }
            },

            $_addInformationAttribute(themeLayer, name) {
                // create array if empty
                if (!themeLayer.informationAttributes) {
                    themeLayer.informationAttributes = [];
                }
                // Add attribute to array
                themeLayer.informationAttributes.push({
                    id: 0,
                    attribute: name,
                    alias: name,
                    order: 0,
                    displayInTable: false,
                    displayInDetail: false,
                    harvest: false,
                    popup: false,
                    hyperlink: false,
                    hyperlinkText: null,
                    hyperlinkExpression: null,
                    hyperlinkImage: false,
                    openExtern: false,
                    dataType: 'xsd:string',
                    exportable: false,
                    print: false,
                    sort: null,
                    isTextAttribute: false,
                    textAttributeShowLabel: false,
                    textAttributeLabel: null,
                    textAttributeSeparator: null,
                    textAttributeOrder: 0,
                    unavailable: false,
                    reportSent: false,
                    errorMessage: ''
                });
            },

            /**
             * Adds additional properties to theme
             * @param theme
             */

            async $_addAdditionalProperties(theme) {
                if (theme?.themeLayers) {
                    for (const themelayer of theme.themeLayers) {
                        themelayer.harvest = true;

                        if (themelayer.filters) {
                            themelayer.filters.forEach((filter) => {
                                filter.attributeErrorMessage = '';
                                filter.operatorErrorMessage = '';
                                filter.valueInputErrorMessage = '';
                                filter.domainErrorMessage = '';
                                filter.domainTypeErrorMessage = '';
                            });
                        }

                        if (themelayer.styles) {
                            themelayer.styles.forEach((style) => {
                                style.hasAdvancedStyleError = false;
                                style.hasAdvancedStyleErrorDomain = false;
                                style.textMaxScale = null;
                            });
                        }

                        if (themelayer.serviceType === OWS_TYPE.OGCT) {
                            themelayer.ogcStyleOptions =
                                await this.$_getOgcStyleOptions(themelayer);
                        }
                    }
                }
                return theme;
            },

            // this is triggered by click in template
            /**
             * Adds a new layer to the theme.
             * Layer data includes styles, graphical layer, etc is read from the backend.
             * A check is done for duplicate layers, in that case the layer is not added.
             */
            async $_addLayer() {
                // Don't add layer when no valid option is selected
                if (this.newThemeLayers.length === 0) {
                    return;
                }
                this.isAddingNewLayer = true;
                if (this.theme.themeLayers === null) {
                    this.theme.themeLayers = [];
                }

                for (const themeLayerName of this.newThemeLayers) {
                    try {
                        const themeLayerId = this.allThemelayers.find(
                            (item) => item.title === themeLayerName
                        ).id;

                        let newLayer = null;
                        let result = await this.$store.dispatch('useApiData', {
                            name: 'GetThemeLayerWithAttributes',
                            parameters: {
                                id: themeLayerId
                            }
                        });
                        if (result) {
                            newLayer = this.$_getNewLayer(result);
                            const doubleLayers = this.theme.themeLayers.filter(
                                (themelayer) => {
                                    return themelayer.id === themeLayerId;
                                }
                            );

                            // Check if layer already exists
                            if (!arrayNotEmpty(doubleLayers)) {
                                // Continue to add themeLayer
                                newLayer.title = themeLayerName;
                                newLayer.harvest = true;
                                this.theme.themeLayers.push(newLayer);

                                // Add default style
                                this.$_addStyle(newLayer, this.$_getGeneratedId());
                            }

                            // Load OGC styles for the new Layer
                            if (newLayer.serviceType === OWS_TYPE.OGCT) {
                                newLayer.uri_OGC = newLayer.serviceUri;
                                newLayer.ogcStyleOptions =
                                    await this.$_getOgcStyleOptions(newLayer);
                            }
                            this.isAddingNewLayer = false;
                            this.newLayerIndex = null;
                        }
                    } catch (error) {
                        this.isAddingNewLayer = false;
                        this.newLayerIndex = null;
                        const errMsg = error.response
                            ? error.response.message
                            : error.message;
                        const data = {
                            message: `Ophalen van laag informatie is mislukt. De foutmelding was: "${errMsg}'onbekend".`,
                            variant: 'danger',
                            title: 'Informatie ophalen mislukt',
                            autoHideDelay: 10000
                        };
                        this.showToast(data);
                    }

                    this.addLayerMode = false;
                }
                this.newThemeLayers = [];
            },

            /**
             * Get a new layer
             * Layer data includes styles, graphical layer, etc is read from the backend.
             */
            $_getNewLayer(result) {
                const newLayer = result.data;

                if (!newLayer.minScaleDenominator) {
                    newLayer.minScaleDenominator = 0.21;
                }
                if (!newLayer.maxScaleDenominator) {
                    newLayer.maxScaleDenominator = 860.16;
                }
                if (!newLayer.filter) {
                    newLayer.filters = [];
                }
                if (newLayer.informationAttributes !== null) {
                    newLayer.informationAttributes.forEach(
                        (informationAttribute) => {
                            // als de alias leeg is, vul deze dan in met de attribute waarde
                            if (!informationAttribute.alias) {
                                informationAttribute.alias = valueToName(
                                    informationAttribute.attribute
                                );
                            }
                            // Set information-attribute dropdown-alias, old (before PBI -8037) information attributes do not always have an alias
                            informationAttribute.selectAlias =
                                informationAttribute.alias;
                        }
                    );
                }
                if (newLayer.geometryTypeIsFromLayer) {
                    newLayer.geometryTypeArray = newLayer.geometryType?.split('');
                }
                if (newLayer.isTransactional) {
                    newLayer.mutateOptionsWfsTArray = [];

                    // Currently only updating (U) is possible (new (N) and delete (R) may be possible in the future)
                    if (newLayer.allowWfsTUpdate) {
                        newLayer.mutateOptionsWfsTArray.push('U');
                    }
                }
                // new layers default visible
                newLayer.visible = true;
                return newLayer;
            },

            async $_addNewStyle(themelayer, generatedId) {
                const rgbaValue = 'rgba(0, 0, 0, 1)';
                const textFontDefaultValue = 'Arial, Helvetica, sans-serif';
                const temporaryId = themelayer.styles.length + generatedId;
                const defaultFillColorSelection =
                    (await this.getOrganizationSetting({
                        name: 'FillColorSelection'
                    })) ?? 'rgba(255, 0, 0, 0.65)';
                const defaultFillColorSelected =
                    (await this.getOrganizationSetting({
                        name: 'FillColorSelected'
                    })) ?? 'rgba(255, 255, 0, 0.65)';
                const defaultStrokeColorSelection =
                    (await this.getOrganizationSetting({
                        name: 'StrokeColorSelection'
                    })) ?? rgbaValue;
                const defaultStrokeColorSelected =
                    (await this.getOrganizationSetting({
                        name: 'StrokeColorSelected'
                    })) ?? rgbaValue;
                const styleItem = {
                    id: temporaryId,
                    // Set default to 23.4375
                    minScale: this.availableScaleItems[19]
                        ? this.availableScaleItems[19].scale
                        : this.availableScaleItems[
                              this.availableScaleItems.length - 1
                          ].scale,
                    // Set default to 12000
                    maxScale: this.availableScaleItems[10]
                        ? this.availableScaleItems[10].scale
                        : this.availableScaleItems[0].scale,
                    textMaxScale: null,
                    // default
                    fillColorDefault: 'rgba(0, 0, 255, 0.25)',
                    strokeColorDefault: rgbaValue,
                    pointSizeDefault: 5,
                    strokeWidthDefault: 1,
                    // text
                    textFillColorDefault: rgbaValue,
                    textStrokeColorDefault: 'rgba(255, 255, 255, 1)',
                    textStrokeWidthDefault: 1,
                    textFontDefault: textFontDefaultValue,
                    textFontSizeDefault: 12,
                    // selected
                    fillColorSelected: defaultFillColorSelected,
                    strokeColorSelected: defaultStrokeColorSelected,
                    pointSizeSelected: 5,
                    strokeWidthSelected: 2,
                    // selection
                    fillColorSelection: defaultFillColorSelection,
                    strokeColorSelection: defaultStrokeColorSelection,
                    pointSizeSelection: 5,
                    strokeWidthSelection: 2,
                    // other
                    customStylingOptions: null,
                    hasAdvancedStyleError: false,
                    hasAdvancedStyleErrorDomain: false,
                    transparency: 0,
                    // Default grafische laag instellen
                    name:
                        themelayer.wmsStyles && themelayer.wmsStyles[0]
                            ? themelayer.wmsStyles[0]
                            : null
                };
                this.$_scaleToResolution(styleItem);
                themelayer.styles.push(styleItem);
            },

            // Stijl toevoegens
            $_addStyle(themelayer, generatedId) {
                if (themelayer.styles === null) {
                    themelayer.styles = [];
                }
                this.$_addNewStyle(themelayer, generatedId);
            },

            $_gotoOverview() {
                this.$router.push('/dashboard/themes');
            },

            $_onCancel() {
                this.$_gotoOverview();
            },

            $_cancelAddLayer() {
                this.addLayerMode = false;
            },

            async $_triggerRemove() {
                try {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'HasWebTheme',
                        parameters: {
                            id: this.theme.id
                        }
                    });
                    if (result?.data) {
                        this.msgBoxOk({
                            message: this.$t('themeCardUsedDeletionsConfirmation')
                        });
                    } else {
                        const data = {
                            message: this.$t('themeCardDeletionsConfirmation')
                        };
                        this.msgBoxConfirm(data).then((confirm) => {
                            if (confirm) {
                                this.$_remove();
                            }
                        });
                    }
                } catch (error) {
                    const data = {
                        message: `${error.response.data.message}`,
                        variant: 'danger',
                        title: 'Theme deletion control',
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            async $_remove() {
                this.isDeleting = true;
                try {
                    await this.$store.dispatch('useApiData', {
                        name: 'DeleteTheme',
                        parameters: {
                            id: this.theme.id
                        }
                    });
                    this.isDeleting = false;
                    this.$_gotoOverview();
                } catch (error) {
                    const data = {
                        message: this.$t('deleteFailureMessage', [
                            error.response.data
                                ? `${error.response.data.message}`
                                : 'onbekend'
                        ]),
                        variant: 'danger',
                        title: this.$t('deleteFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                    this.isDeleting = false;
                }
            },

            getDomainKeyValues(filter) {
                this.activeFilter = filter;
                if (filter.domainId && filter.domainId > 0) {
                    this.$store
                        .dispatch('useApiData', {
                            name: 'GetDomainKeyValues',
                            parameters: {
                                id: filter.domainId
                            }
                        })
                        .then((result) => {
                            this.domainKeyValues = Object.assign([], result.data);
                        });
                }
            },

            /**
             * Domaintype selection list changed event
             * If changed to Dropdown- or Suggestion-list and filter value is null take the empty value as default
             * @param {any} filter current filter object
             */
            $_domainTypeSelected(event, filter) {
                filter.domainType = event;
                this.$_validateFilterDomainType(filter);
                if (
                    filter.domainType === DOMAIN_TYPE.DROPDOWNLIST ||
                    filter.domainType === DOMAIN_TYPE.SUGGESTIONLIST
                ) {
                    filter.value = null;
                }

                if (filter.domainType === DOMAIN_TYPE.ALL_VALUES) {
                    filter.operatorEditable = false;
                }
            },

            $_domainTypeValueOptions(filter) {
                if (this.domainKeyValues === null) {
                    if (filter.domainId && filter.domainId > 0) {
                        this.$store
                            .dispatch('useApiData', {
                                name: 'GetDomainKeyValues',
                                parameters: {
                                    id: filter.domainId
                                }
                            })
                            .then((result) => {
                                this.domainKeyValues = Object.assign(
                                    [],
                                    result.data
                                );
                            });
                    }
                }

                if (arrayNotEmpty(this.domainKeyValues)) {
                    return convertToValueTextPair(
                        this.domainKeyValues,
                        'key',
                        'value'
                    ).sort(arraySortAscendingFunction('text'));
                }
                // Solving Sonarqube code smell
                return [];
            },

            $_exists: debounce(async function () {
                const themeParams = {
                    name: this.theme.name,
                    themeId: this.id > 0 ? this.id : 0
                };
                const result = await this.$store.dispatch('useApiData', {
                    name: 'ThemeExists',
                    data: themeParams
                });
                this.errorInUse = result?.data;
            }, 750),

            $_filterAttributeOptions(informationAttributes) {
                if (arrayNotEmpty(informationAttributes)) {
                    const attributes = informationAttributes.map((item) => {
                        return {
                            attribute: item.attribute,
                            alias: item.alias ? item.alias : item.attribute
                        };
                    });
                    const sortedOptions = orderBy(attributes, [
                        (attribute) => attribute.alias.toLowerCase()
                    ]);
                    return convertToValueTextPair(
                        sortedOptions,
                        'attribute',
                        'alias'
                    );
                }
                // Solving Sonarqube code smell
                return [];
            },

            /**
             * Zet filter unavailable op false wanneer een bestaande filterwaarde is gekozen
             * Sla information attribute id op in het filter
             * @param {object} $scope het AngularJS scope object
             * @param {object} filter het filter object
             * @param {object} themelayerInformationAttributes de  information attributes van de themelayer
             * @param {object} toaster, meldingen venster
             */
            $_filterAttributeSelected(event, filter, themelayer) {
                const themelayerInformationAttributes =
                    themelayer.informationAttributes;
                filter.filterAttribute = event;
                if (filter.filterAttribute) {
                    // Find id for the chosen information attribute
                    const selectedInformationAttribute =
                        themelayerInformationAttributes.find(
                            (item, index, attr) => {
                                if (item.attribute === filter.filterAttribute) {
                                    return attr;
                                } else {
                                    return false;
                                }
                            }
                        );

                    filter.informationAttributeId = selectedInformationAttribute.id;
                    filter.dataType = selectedInformationAttribute.dataType;

                    const classifiedDataType = this.$_classifyFilterDataType(
                        selectedInformationAttribute.dataType
                    );
                    // zet de lijst van mogelijke operatoren
                    filter.possibleOperators = getListPossibleOperators(
                        classifiedDataType,
                        isInputDomainAndTypeAllValues(filter)
                    );
                    // Zet de lijst van mogelijk input waarden
                    filter.possibleInputTypes =
                        this.$_getListPossibleInputTypes(classifiedDataType);
                    // Set unavailable to false if new value is selected
                    if (filter.unavailable && filter) {
                        filter.unavailable = false;
                    }
                    // Set the name of filter automatically
                    this.$_setFilterName(filter, selectedInformationAttribute);
                    this.$_filterNameValidate(filter, themelayer);
                }
                this.$_validateFilterAttribute(filter);
            },

            $_filterDomainOptions() {
                if (arrayNotEmpty(this.domains)) {
                    const ordedOptions = orderBy(this.domains, [
                        (domain) => domain.name.toLowerCase()
                    ]);
                    return convertToValueTextPair(ordedOptions, 'id', 'name');
                }
                return [];
            },

            /**
             * Domain selection list changed event handler
             * @param {object} filter current filter object
             */
            async $_filterDomainSelected(input, filter) {
                filter.domainId = input;
                this.$_validateFilterDomain(filter);

                if (filter.domainId) {
                    // Get details for selected domain
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetFeedbackDomain',
                        parameters: {
                            id: filter.domainId
                        }
                    });

                    filter.domain = result?.data;
                    this.domainKeyValues = null;
                    this.$_domainTypeValueOptions(filter);
                    // Domain selected, empty filter value
                    filter.value = '';
                } else {
                    filter.domain = input;
                }
            },

            $_filterInputTypeOptions(filter) {
                const classifiedDataType = this.$_classifyFilterDataType(
                    filter.dataType
                );
                const possibleInputTypes =
                    this.$_getListPossibleInputTypes(classifiedDataType);
                return convertToValueTextPair(
                    possibleInputTypes,
                    'code',
                    'description'
                );
            },

            /**
             * Valideer de filter naam
             * @param  {object} filter filter welke waarde gevalideerd wordt
             */
            $_filterNameValidate(filter, themelayer) {
                filter.errorMessage = '';
                this.filterNameErrorMessage = '';
                const nameisUnique = validateFilterNameUnique(
                    filter.name,
                    themelayer
                );
                const nameisEmpty = filter.filterAttribute && filter.name === '';
                if (!nameisUnique || nameisEmpty) {
                    if (nameisEmpty) {
                        this.filterNameErrorMessage = this.$t('requiredField');
                    } else if (!nameisUnique) {
                        this.filterNameErrorMessage = `Een filter met de naam '${
                            filter.name ? filter.name : filter.filterAttribute
                        }' bestaat al`;
                    }
                    filter.errorMessage = this.filterNameErrorMessage;
                }
            },

            $_filterOperatorOptions(filter) {
                return filterOperatorOptions(filter);
            },

            $_onSelectDomainValue(event, filter) {
                filter.value = event;
                this.$_filterUpdateVisibility(filter);
            },

            $_onChangeInputValue(event, filter) {
                filter.value = event;
                this.$_filterUpdateVisibility(filter);
            },

            /**
             * Update filter visibility, true when value is empty
             * @param  {object} filter filter welke waarde gevalideerd wordt
             */
            $_filterUpdateVisibility(filter) {
                if (filter.value === '') {
                    filter.visible = true;
                }
            },

            /**
             * Update filter selected boolean, selected when visisble is false
             * @param  {object} filter filter welke waarde gevalideerd wordt
             */
            $_filterUpdateSelected(filter) {
                if (filter.visible === false) {
                    filter.selected = true;
                }
            },

            /**
             * Valideer filter waarde
             * @param  {object} filter filter welke waarde gevalideerd
             */
            $_filterValidate(filter) {
                // datum validatie
                if (filter.valueInput === VALUE_INPUT_TYPE.DATEFIELD) {
                    // controle op datuminvoer
                    const valid = validateDate(filter.value, true);
                    // indien niet valide, toont dat aan de gebruiker
                    if (valid) {
                        this.invalidFilters = false;
                        this.invalidDateMessage = '';
                    } else {
                        this.invalidFilters = true;
                        this.invalidDateMessage = this.$t('labelInvalidDate');
                    }
                }
                // valideer filter waarde
                if (!stringNotEmpty(filter.value)) {
                    // zet het vinkje visible aan
                    for (const themeLayer of this.theme.themeLayers) {
                        for (const themeLayerFilter of themeLayer.filters) {
                            if (themeLayerFilter.id === filter.id) {
                                themeLayerFilter.visible = true;
                                break;
                            }
                        }
                    }
                    filter.visible = true;
                }
            },

            async $_getAllDomains() {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetFeedbackDomainOverview'
                });
                // Make object properties reactive for Vue
                this.domains = Object.assign([], result.data);
            },

            $_getAllOperators() {
                getRefCodes(OPERATOR_TYPE.KEY).then((result) => {
                    this.allOperators = result.data;
                });
            },

            /**
             * generate a temporary id for new filters (for Selenium selection)
             * @returns {number} the temporary id used for Selenium selection
             */
            $_getGeneratedId() {
                // Solving Sonarqube code smell
                this.generatedId++;
                return `id${this.generatedId}`;
            },

            /**
             * Afhankelijk van het dataType wordt een lijst met invoer types geleverd
             * @param  {string} classifiedDataType voor welke de lijst van operatoren wordt samengesteld
             * @return {array}  lijst met invoertypen
             */
            $_getListPossibleInputTypes(classifiedDataType) {
                switch (classifiedDataType) {
                    case 'string':
                    case 'boolean':
                    case 'integer':
                    case 'float':
                        return this.allValueInputTypes.filter((o) =>
                            [
                                VALUE_INPUT_TYPE.TEXTFIELD,
                                VALUE_INPUT_TYPE.DOMAIN
                            ].includes(o.code)
                        );
                    case 'datetime':
                        return this.allValueInputTypes.filter((o) =>
                            [VALUE_INPUT_TYPE.DATEFIELD].includes(o.code)
                        );
                    default:
                        return [];
                }
            },

            /**
             * Get maximum number of characters that can be entered
             * Numeric: 20
             * Alfanumeric: 100
             * Date: 15
             * @param  {object} filter the filter
             * @return {[type]} maximum number of characters
             */
            $_getMaxInputChars(filter) {
                // Geen check op domein waarden
                if (filter) {
                    const classifiedDataType = this.$_classifyFilterDataType(
                        filter.dataType
                    );
                    switch (classifiedDataType) {
                        case 'string':
                            return 100;
                        case 'integer':
                        case 'float':
                            return 20;
                        /* Date input can be a regular date (i.e. '30-01-2019') or a sysdate comparison (i.e. '<SYSDATE-123>') */
                        case 'datetime':
                            return 15;
                        default:
                            return 20;
                    }
                }
                // Solving Sonarqube code smell
                return 20;
            },

            $_getStyleLable(themelayer) {
                return themelayer.graphicalLayerName &&
                    themelayer.graphicalLayerName !== ''
                    ? this.$t('labelGraphicStyle')
                    : this.$t('labelStyle');
            },

            /**
             * Classifies the datatypes to either 1) string, 2) boolean, 3) integer, 4) float, 5) datetime or 6) unknown
             * @param  {string} dataType the real dataType as retrieved from the OGC Web Service
             * @return {string} the classified dataType, as used in NedGlobe Dashboard
             */
            $_classifyFilterDataType(dataType) {
                return classifyDataType(dataType);
            },

            /**
             * Validates the attribute field of a filter attribute
             * @param filter
             */
            $_validateFilterAttribute(filter) {
                filter.attributeErrorMessage = !filter.filterAttribute
                    ? this.$t('requiredField')
                    : '';
            },

            /**
             * Validates the operator field of a filter attribute
             * @param filter
             */
            $_validateFilterOperator(filter) {
                filter.operatorErrorMessage = !filter.operator
                    ? this.$t('requiredField')
                    : '';
            },

            /**
             * Validates the valueInput field of a filter attribute
             * @param filter
             */
            $_validateFilterInputType(filter) {
                filter.valueInputErrorMessage = !filter.valueInput
                    ? this.$t('requiredField')
                    : '';
            },

            /**
             * Validates the domain field of a filter attribute
             * @param filter
             */
            $_validateFilterDomain(filter) {
                filter.domainErrorMessage =
                    filter.valueInput === VALUE_INPUT_TYPE.DOMAIN &&
                    !filter.domainId
                        ? this.$t('requiredField')
                        : '';
            },

            /**
             * Return the calculated state for the domainType field of a filter attribute
             * @param filter
             */
            $_validateFilterDomainType(filter) {
                filter.domainTypeErrorMessage =
                    filter.domainId && filter.domainType === null
                        ? this.$t('requiredField')
                        : '';
            },

            async $_getTheme() {
                this.loading = true;
                nprogress.start();
                if (this.inEditMode) {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetThemeDetail',
                        parameters: {
                            id: this.id
                        }
                    });
                    const data = this.$_fillEmptyAliases(result);
                    const theme = await this.$_addAdditionalProperties(data);
                    // Make object properties reactive for Vue
                    this.theme = Object.assign({}, theme);
                    this.$_checkErrorsForTheme();
                    this.$_validateStyles();
                    this.$_checkMissingInformationAttributes();
                    if (this.theme.unavailable) {
                        this.$_checkErrorsForAllThemeLayers(false);
                        this.$_revealThemeErrorMessages();
                    }
                    this.$_checkMissingFilterAttributes();
                    // Set geometry types from themelayer geometrytype code
                    this.$_getGeometryTypes();
                    this.$_getWfsTMutateOptions();
                    this.loading = false;
                    nprogress.done();
                } else {
                    // this.id will be -1
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetThemeDetail',
                        parameters: {
                            id: this.id
                        }
                    });
                    // Make object properties reactive for Vue
                    this.theme = Object.assign({}, result.data);
                    this.loading = false;
                    nprogress.done();
                }
            },

            /**
             * Give alias a value when empty
             */
            $_fillEmptyAliases({ data }) {
                return {
                    ...data,
                    themeLayers: data?.themeLayers?.map((themeLayer) => {
                        themeLayer.informationAttributes =
                            themeLayer?.informationAttributes?.map(
                                (informationAttribute) => {
                                    if (!informationAttribute.alias) {
                                        informationAttribute.alias = valueToName(
                                            informationAttribute.attribute
                                        );
                                    }
                                    return informationAttribute;
                                }
                            );
                        return themeLayer;
                    })
                };
            },

            $_graphicalLayerOptions(themelayer) {
                let options = [];
                if (themelayer) {
                    // For WMS/WFS layers use themelayer.wmsStyleTitles
                    if (themelayer.serviceType != OWS_TYPE.OGCT) {
                        if (arrayNotEmpty(themelayer.wmsStyleTitles)) {
                            options = themelayer.wmsStyleTitles.map(
                                (item, index) => {
                                    return {
                                        value: themelayer.wmsStyles[index],
                                        text: item
                                    };
                                }
                            );
                        }
                    } else if (arrayNotEmpty(themelayer.ogcStyleOptions)) {
                        // for OGCT layer
                        options = themelayer.ogcStyleOptions;
                    }
                }
                return options;
            },

            async $_getOgcStyleOptions(themelayer) {
                if (themelayer) {
                    return getOgcStyleOptions(this.$store, themelayer.uri_OGC);
                } else {
                    return [];
                }
            },

            /**
             * Set the error flag of the theme layers that contain filter attributes for which the attribute does not exist anymore.
             */
            $_checkMissingFilterAttributes() {
                if (this.theme.themeLayers) {
                    for (const themeLayer of this.theme.themeLayers) {
                        if (themeLayer.missingFilterAttributes) {
                            themeLayer.error = true;
                        }
                    }
                }
            },

            /**
             * Set the error flag of the theme layers that contain attributes that do not exist anymore.
             *
             */
            $_checkMissingInformationAttributes() {
                if (this.theme.themeLayers) {
                    this.theme.themeLayers.forEach(function (themeLayer) {
                        if (themeLayer.missingInformationAttributes) {
                            themeLayer.error = true;
                        }
                    });
                }
            },

            $_informationAttributesOptions(themelayer) {
                if (themelayer && arrayNotEmpty(themelayer.informationAttributes)) {
                    return themelayer.informationAttributes.map((item) => {
                        return { value: item.attribute, text: item.attribute };
                    });
                }
                // Solving Sonarqube code smell
                return [];
            },

            $_initFilterAttributeData() {
                // Retrieve possible operator types
                getRefCodes(OPERATOR_TYPE.KEY).then((result) => {
                    this.allOperators = result.data;
                });

                // Retrieve possible value-input types
                getRefCodes(VALUE_INPUT_TYPE.KEY).then((result) => {
                    this.allValueInputTypes = result.data;
                });

                // Retrieve possible domain types
                getRefCodes(DOMAIN_TYPE.KEY).then((result) => {
                    const refCodes = result.data;
                    this.allDomainTypes = refCodes.filter(
                        (rf) => rf.code !== DOMAIN_TYPE.SELECT
                    );
                });

                // Get all domains
                this.$_getAllDomains();
            },

            /**
             * Check if filter datatype is alphanumeric
             * @param  {object} filter the filter
             * @return {boolean} true if datatype is alphanumeric
             */
            $_isDataTypeAlphaNumeric(filter) {
                return this.$_classifyFilterDataType(filter.dataType) === 'string';
            },

            /**
             * Get all themelayers, retrieve only once per scope
             */
            async $_loadThemelayers() {
                this.addLayerMode = true;
                if (this.allThemelayers === null) {
                    const result = await this.$store.dispatch('useApiData', {
                        name: 'GetThemeLayers'
                    });
                    this.allThemelayers = Object.assign([], result.data);
                    this.$_themelayerOptions();
                } else {
                    // Themelayers are already loaded
                    this.$_themelayerOptions();
                }
            },

            async $_loadThemeGroups() {
                if (this.themeGroupOptions.length === 0) {
                    const themeGroupsResult = await this.$store.dispatch(
                        'useApiData',
                        {
                            name: 'GetThemeGroups',
                            parameters: {}
                        }
                    );
                    this.themeGroupOptions = Object.assign(
                        [],
                        themeGroupsResult.data
                    );
                    this.themeGroupOptions?.sort(
                        arraySortAscendingFunction('name', false)
                    );
                }
            },

            /**
             * Sets the generated domain as selected
             * @param data
             */
            $_onModalGeneratedDomain(data) {
                this.domains.push({
                    errorMessage: '',
                    id: data.domain.id,
                    name: data.domain.name,
                    unavailable: false
                });
                this.$_filterDomainSelected(data.domain.id, data.domain);
                this.savedFilter.domainId = data.domain.id;
            },

            $_onModalDomainSaved(data) {
                if (this.savedFilter !== null) {
                    // Set the domain id of the active filter
                    this.savedFilter.domainId = data.id;
                    // Refresh domain list
                    this.$_getAllDomains();
                    const message = this.getDomainModalMessage(data);
                    if (message) {
                        this.showToast({
                            message: message,
                            variant: 'danger',
                            autoHideDelay: 5000,
                            noCloseButton: true
                        });
                    }
                    // Change the domain selection
                    this.$_filterDomainSelected(
                        this.savedFilter.domainId,
                        this.savedFilter
                    );
                    // Reset the saved filter
                    this.savedFilter = null;
                }
            },

            $_themeGroupSelected(event) {
                this.theme.themeGroupId = event;
            },

            $_onSelectFilterOperatorOption(event, filterattr) {
                filterattr.operator = event;
                this.$_validateFilterOperator(filterattr);
            },

            $_onSelectMinScale(event, style) {
                style.minScale = event;
                this.$_scaleToResolution(style);
                // reset scale for text when min/max scale for layer is changed
                style.textMaxScale = null;
            },

            $_onSelectMaxScale(event, style) {
                style.maxScale = event;
                this.$_scaleToResolution(style);
                // reset scale for text when min/max scale for layer is changed
                style.textMaxScale = null;
            },

            $_onSelectTextMaxScale(event, style) {
                style.textMaxScale = event;
                this.$_scaleToResolution(style);
            },

            $_onSelectWmsStyleName(event, style) {
                style.name = event;
            },

            $_onSubmit() {
                if (this.$refs.themeform.checkValidity()) {
                    this.$_save();
                }
            },

            $_openDomainModal(filter) {
                // The prop value of Modal needs to be changed in order to trigger
                // the Watch function in AddDomainModal component
                this.launchModal = !this.launchModal;
                this.savedFilter = filter;
            },

            $_removeAttribute(attribute) {
                //Implement this function
            },

            $_removeFilter(filters, index) {
                const data = {
                    message: 'Weet u zeker dat u dit filter wilt verwijderen?'
                };
                this.msgBoxConfirm(data).then((confirm) => {
                    if (confirm) {
                        filters.splice(index, 1);
                    }
                });
            },

            // Layer verwijderen
            $_removeLayer(layerIndex) {
                const data = {
                    message: 'Weet u zeker dat u deze laag wilt verwijderen?'
                };
                this.msgBoxConfirm(data).then((confirm) => {
                    if (confirm) {
                        this.theme.themeLayers.splice(layerIndex, 1);
                    }
                });
            },

            /**
             * Converts the min and max resolution to min and max scale
             * @param {object} styleItem - style
             */
            $_resolutionToScale(styleItem) {
                if (this.availableScaleItems) {
                    const max = this.availableScaleItems.filter((scale) => {
                        return scale.resolution === styleItem.maxResolution;
                    });

                    const min = this.availableScaleItems.filter((scale) => {
                        return scale.resolution === styleItem.minResolution;
                    });

                    const maxText = this.availableScaleItems.filter(
                        ({ resolution }) => {
                            return resolution === styleItem.textMaxResolution;
                        }
                    );

                    if (arrayNotEmpty(max)) {
                        styleItem.maxScale = max[0].scale;
                    }

                    if (arrayNotEmpty(min)) {
                        styleItem.minScale = min[0].scale;
                    }

                    // null value is allowed
                    styleItem.textMaxScale = arrayNotEmpty(maxText)
                        ? maxText[0].scale
                        : null;
                }
            },

            $_resetFieldValues() {
                if (this.theme.themeLayers) {
                    for (const themeLayer of this.theme.themeLayers) {
                        // Reset filters
                        this.$_resetFieldValuesFilters(themeLayer);

                        // Reset styles
                        themeLayer.styles.forEach((styleItem) => {
                            if (
                                styleItem.pointSizeDefault === 'null' ||
                                styleItem.pointSizeDefault === ''
                            ) {
                                styleItem.pointSizeDefault = null;
                            }
                        });
                    }
                }
            },

            $_resetFieldValuesFilters(themeLayer) {
                if (themeLayer.filters) {
                    for (const filter of themeLayer.filters) {
                        switch (filter.domainType) {
                            // Remove old filter.value if filter.domainType = 'Alle waarden tonen'
                            // 'Alle waarden tonen' selects default all domain values, not a single value
                            // The filter values are not stored into the database, they only exist on the filter-tab in Mobile and Web
                            case DOMAIN_TYPE.ALL_VALUES:
                                filter.value = null;
                                break;
                            // Save null filter value as empty string for 'Suggestielijst' & 'Dropdownlijst'
                            case DOMAIN_TYPE.SUGGESTIONLIST:
                            // Fall through
                            case DOMAIN_TYPE.DROPDOWNLIST:
                                if (filter.value === null) {
                                    filter.value = '';
                                }
                                break;
                        }
                    }
                }
            },

            /*
             * Flushes the array of errors
             */
            $_revealThemeErrorMessages() {
                if (this.themeErrorMessagesList.length > 0) {
                    for (
                        let index = 0;
                        index < this.themeErrorMessagesList.length &&
                        index < this.maxNumberOfErrors;
                        index++
                    ) {
                        const data = {
                            message: this.themeErrorMessagesList[index].message,
                            variant: 'danger',
                            noCloseButton: true,
                            autoHideDelay: 8000
                        };
                        this.showToast(data);
                    }
                }
            },

            /**
             * Theme save functie
             * @return {boolean} True if save succeeded, false otherwise
             */
            async $_save() {
                this.themeErrorMessagesList = [];
                // Validate styles
                const stylesAreInvalid = this.$_validateStyles();
                const filtersAreInvalid = this.$_validateFilterAttributes();
                // Validate geometryTypes
                const geometryTypesAreInvalid = this.$_validateGeometryType();

                if (
                    stylesAreInvalid ||
                    filtersAreInvalid ||
                    geometryTypesAreInvalid
                ) {
                    this.$_checkErrorsForAllThemeLayers(true);
                    this.$_revealThemeErrorMessages();
                    return;
                }

                // Validate filters
                if (this.invalidFilters) {
                    return;
                }
                // Before saving, call function to save (possible) changes
                // in order of attributes by all themelayers
                if (this.$refs.informationTab?.length > 0) {
                    this.$refs.informationTab.forEach((informationTab) => {
                        informationTab.$_onClickChangeOrder();
                    });
                }
                // Before saving, call function to save (possible) changes
                // in order of attributes by all themelayers
                if (this.$refs.advancedLabelTab?.length > 0) {
                    this.$refs.advancedLabelTab.forEach((advLabelTab) => {
                        advLabelTab.$_onClickChangeOrder();
                    });
                }

                // Check geometrytype, set order to 'PLV'
                if (this.theme.themeLayers) {
                    this.$_setGeometryTypeOrder();
                    // Remove informationAttributes for queryable layers that have showFeatureInfo set to false
                    this.$_removeInformationAttributes();
                }

                const added = !this.theme.id;
                this.isSaving = true;
                this.$_resetFieldValues();
                const data = cleanData(this.theme);
                try {
                    this.$store
                        .dispatch('useApiData', {
                            name: 'SaveTheme',
                            data
                        })
                        .then((result) => {
                            if (result?.data) {
                                this.isSaving = false;
                                // show result
                                const data = {
                                    message: this.$t('themeCardSavedConfirmation'),
                                    variant: 'success',
                                    autoHideDelay: 2500,
                                    noCloseButton: true
                                };
                                this.showToast(data);
                                // reset errors
                                this.theme.error = '';
                                this.themeErrorMessagesList = [];
                                this.theme.unavailable = false;
                                // if new thema goto overview
                                if (added) {
                                    this.$_gotoOverview();
                                } else {
                                    this.$_init();
                                }
                            }
                        });
                } catch (error) {
                    this.isSaving = false;
                    // toaster show error
                    const errMsg = error.response?.data.message
                        ? error.response.data.message
                        : error.message;
                    const data = {
                        message: errMsg,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 10000
                    };
                    this.showToast(data);
                }
            },

            $_removeInformationAttributes() {
                this.theme.themeLayers
                    .filter((tl) => tl.queryable && !tl.showFeatureInfo)
                    .forEach((themelayer) => {
                        themelayer.informationAttributes = [];
                    });
            },

            /**
             * Converts the min and max scale to min and max resolution
             * @param {object} styleItem - style
             */
            $_scaleToResolution(styleItem) {
                const max = this.availableScaleItems.filter((scaleItem) => {
                    return scaleItem.scale === styleItem.maxScale;
                });

                const min = this.availableScaleItems.filter((scaleItem) => {
                    return scaleItem.scale === styleItem.minScale;
                });

                const maxText = this.availableScaleItems.filter(({ scale }) => {
                    return scale === styleItem.textMaxScale;
                });

                if (arrayNotEmpty(max)) {
                    styleItem.maxResolution = max[0].resolution;
                }

                if (arrayNotEmpty(min)) {
                    styleItem.minResolution = min[0].resolution;
                }

                // null value is allowed
                styleItem.textMaxResolution = arrayNotEmpty(maxText)
                    ? maxText[0].resolution
                    : null;
            },

            /**
             * Sets the name of the given filter
             * @param filter
             * @param selectedInformationAttribute
             */
            $_setFilterName(filter, selectedInformationAttribute) {
                if (selectedInformationAttribute.alias) {
                    filter.name = valueToName(selectedInformationAttribute.alias);
                } else {
                    filter.name = valueToName(
                        selectedInformationAttribute.attribute
                    );
                }
            },

            $_hasWfsServiceType(themelayer) {
                return themelayer && themelayer.serviceType === OWS_TYPE.WFS;
            },
            $_isWfsTLayer(themeLayer) {
                return (
                    themeLayer &&
                    themeLayer.serviceType === OWS_TYPE.WFS &&
                    themeLayer.isTransactional
                );
            },
            $_hasWmsAttributes(themelayer) {
                return (
                    themelayer.serviceType === OWS_TYPE.WMS &&
                    themelayer.informationAttributes &&
                    themelayer.informationAttributes.length > 0
                );
            },

            $_isWfsWithGraphicalLayer(themelayer) {
                return (
                    themelayer.informationAttributes &&
                    themelayer.informationAttributes.length > 0 &&
                    this.$_hasWfsServiceType(themelayer) &&
                    this.$_showGrafischeLaag(themelayer)
                );
            },

            $_showGrafischeLaag(themelayer) {
                if (themelayer) {
                    if (themelayer.serviceType === OWS_TYPE.OGCT) {
                        return true;
                    }
                    // Hide if no WMS styles known
                    if (themelayer.wmsStyles === null) {
                        return false;
                    }
                    // Hide if WFS layer without graphicalLayerName
                    if (
                        themelayer.serviceType === OWS_TYPE.WFS &&
                        themelayer.graphicalLayerName === null
                    ) {
                        return false;
                    }
                    // Show if WMS layer or when graphicalLayerName is defined
                    return (
                        themelayer.serviceType === OWS_TYPE.WMS ||
                        themelayer.graphicalLayerName !== ''
                    );
                } else {
                    // Not supposed to happen
                    return false;
                }
            },

            /**
             * Check theme for errors
             */
            $_checkErrorsForTheme() {
                if (this.theme.unavailable) {
                    // error from theme
                    if (stringNotEmpty(this.theme.errorMessage)) {
                        this.themeErrorMessagesList.push({
                            message: `${this.theme.errorMessage}`
                        });
                    }
                    // errors from theme layers
                    this.theme.themeLayers.forEach((themeLayer) => {
                        if (stringNotEmpty(themeLayer.errorMessage)) {
                            this.themeErrorMessagesList.push({
                                message: `${themeLayer.errorMessage}`
                            });
                        }
                    });
                }
            },

            /**
             * Show an error if:
             * 1. Theme layer has no styles.
             * 2. There is an attribute in the database not available in WFS.
             */
            $_checkErrorsForThemeLayer(themeLayer, isSaving) {
                if (!themeLayer.errorMessage && !themeLayer.error) {
                    return;
                }
                // Check if minimal 1 style is defined
                if (!arrayNotEmpty(themeLayer.styles)) {
                    this.themeErrorMessagesList.push({
                        message: `Voor themalaag '${themeLayer.title}' moet nog minimaal 1 stijl worden aangemaakt.`
                    });
                } else {
                    // Check if styles contains error
                    themeLayer.styles.forEach((style) => {
                        if (style.error) {
                            this.themeErrorMessagesList.push({
                                message: `Voor themalaag '${themeLayer.title}' zijn niet alle velden voor Stijl correct ingevuld: ${style.error}`
                            });
                        }
                    });
                }

                // Check Information attributes but not during saving because saving will solve the error
                if (!isSaving && themeLayer.missingInformationAttributes) {
                    this.themeErrorMessagesList.push({
                        message: `Thema laag '${themeLayer.title}' bevat attributen die niet beschikbaar zijn.\nSla het thema op om de attributen uit de lagen te verwijderen.`
                    });
                }

                if (themeLayer.missingFilterAttributes) {
                    this.themeErrorMessagesList.push({
                        message: `Niet alle verplichte filterattribuut velden zijn correct ingevuld voor thema laag '${themeLayer.title}'.`
                    });
                }

                // Check if geometryType is filled
                if (themeLayer.missingGeometryType) {
                    this.themeErrorMessagesList.push({
                        message: `${this.$t(
                            'geomType'
                        )} is niet ingevuld voor thema laag '${themeLayer.title}'.`
                    });
                }
            },

            /**
             * Show an error if:
             * 1. Theme layer has no styles.
             * 2. There is an attribute in the database not available in WFS.
             */
            $_checkErrorsForAllThemeLayers(isSaving) {
                if (this.theme.themeLayers) {
                    for (const i in this.theme.themeLayers) {
                        this.$_checkErrorsForThemeLayer(
                            this.theme.themeLayers[i],
                            isSaving
                        );
                    }

                    // Check Filters
                    if (this.invalidFilters) {
                        this.themeErrorMessagesList.push({
                            message: 'Niet alle ingestelde filters zijn valide.'
                        });
                    }
                }
            },

            $_themelayerOptions() {
                if (arrayNotEmpty(this.allThemelayers)) {
                    // Select all themelayers including baselayers
                    let themeLayers = this.allThemelayers;
                    // Order by title
                    themeLayers = orderBy(themeLayers, [
                        (themeLayer) => themeLayer.title.toLowerCase()
                    ]);
                    // Make sorted themelayers available for other methods
                    this.themelayerOptions = themeLayers;
                    // Convert to value: id, text: title for b-form-select
                    themeLayers = convertToValueTextPair(
                        themeLayers,
                        'id',
                        'title'
                    );
                    // Determine the add layer mode state
                    this.addLayerMode = arrayNotEmpty(this.allThemelayers);
                    return themeLayers;
                }
                // Solving Sonarqube code smell
                return [];
            },

            $_validateFilterAttributes() {
                let invalid = false;
                if (this.theme.themeLayers) {
                    this.theme.themeLayers.forEach((themeLayer) => {
                        themeLayer.missingFilterAttributes = false;

                        themeLayer.filters.forEach((filter) => {
                            if (
                                !filter.filterAttribute ||
                                !filter.operator ||
                                !filter.valueInput ||
                                stringNotEmpty(filter.errorMessage) ||
                                (filter.valueInput === VALUE_INPUT_TYPE.DOMAIN &&
                                    !filter.domainType)
                            ) {
                                this.$_validateFilterAttribute(filter);
                                this.$_validateFilterOperator(filter);
                                this.$_validateFilterInputType(filter);
                                this.$_validateFilterDomain(filter);
                                this.$_validateFilterDomainType(filter);
                                invalid = true;
                                themeLayer.error = true;
                                themeLayer.missingFilterAttributes = true;
                            }
                        });
                    });
                }
                return invalid;
            },

            $_validateGeometryType() {
                let invalid = false;
                if (this.theme.themeLayers) {
                    this.theme.themeLayers.forEach((themeLayer) => {
                        themeLayer.missingGeometryType = false;

                        if (
                            this.$_hasWfsServiceType(themeLayer) &&
                            (!themeLayer.geometryType ||
                                themeLayer.geometryType.length === 0)
                        ) {
                            invalid = true;
                            themeLayer.error = true;
                            themeLayer.missingGeometryType = true;
                        }
                    });
                }
                return invalid;
            },

            /**
             * Check if the min/max scale are valid
             * @return {boolean} true if errors found, otherwise false
             */
            $_checkMinMaxScale(minScale, maxScale) {
                if (!minScale || !maxScale || minScale <= 0 || maxScale <= 0) {
                    return 'Geen schaalbereik ingevuld.';
                } else if (minScale >= maxScale) {
                    return 'Minimum schaalbereik is niet kleiner dan maximum.';
                } else {
                    return '';
                }
            },

            /**
             * Validate the styles of a themelayer
             * @return {boolean} true if errors found, otherwise false
             */
            $_validateThemeLayerStyles(themeLayer) {
                let invalid = false;
                // Set to false for new themelayer validation
                let currentLayerInvalid = false;
                if (arrayNotEmpty(themeLayer.styles) && themeLayer.wmsStyles) {
                    if (
                        themeLayer.styles[0].name === null ||
                        typeof themeLayer.styles[0].name === 'undefined'
                    ) {
                        // Use first style if it exists
                        // If no styles exist use an empty style, the OWS GetMap will set parameter &styles= for an empty WMS style
                        themeLayer.styles[0].name = themeLayer.wmsStyles[0]
                            ? themeLayer.wmsStyles[0]
                            : '';
                    }
                }

                if (arrayNotEmpty(themeLayer.styles)) {
                    // Check all styles
                    themeLayer.styles.forEach((style) => {
                        this.$_resolutionToScale(style);
                        style.error = this.$_checkMinMaxScale(
                            style.minScale,
                            style.maxScale
                        );
                        // Min scale en max scale must be filled
                        if (style.error !== '') {
                            invalid = true;
                            currentLayerInvalid = true;
                        } else if (
                            !this.$_isDefaultStrokeColorOK(themeLayer, style) ||
                            this.$_hasInvalidAdvancedStyle(style) ||
                            this.$_hasInvalidAdvancedStyleDomain(style)
                        ) {
                            style.error = this.$t('errorRequiredFieldsUnset');
                            invalid = true;
                            currentLayerInvalid = true;
                        }
                    });
                } else {
                    invalid = true;
                    currentLayerInvalid = true;
                }
                // Set the layer error only if currentLayerInvalid is set
                themeLayer.error = currentLayerInvalid;
                return invalid;
            },

            /**
             * Validate the styles of all the themelayers of the theme
             * @return {boolean} true if errors found, otherwise false
             */
            $_validateStyles() {
                let invalid = false;
                // Use first graphical layer style if no style is selected
                if (this.theme.themeLayers) {
                    this.theme.themeLayers.forEach((themeLayer) => {
                        if (this.$_validateThemeLayerStyles(themeLayer)) {
                            invalid = true;
                        }
                    });
                }
                return invalid;
            },

            /**
             *  For a WFS layer without a graphical layer the Standard stroke color should be filled. Also for all WFS layers the Selection and Detail stroke color should be filled
             */
            $_isDefaultStrokeColorOK(themeLayer, style) {
                if (themeLayer.serviceType === OWS_TYPE.WFS) {
                    const isColorSelectedOK =
                        stringNotEmpty(style.strokeColorSelection) &&
                        stringNotEmpty(style.strokeColorSelected);

                    if (stringNotEmpty(themeLayer.graphicalLayerName)) {
                        return isColorSelectedOK;
                    } else {
                        return (
                            stringNotEmpty(style.strokeColorDefault) &&
                            isColorSelectedOK
                        );
                    }
                } else {
                    return true;
                }
            },

            /**
             * Keuze gemaakt tussen: Domein, Tekstveld of Datumveld.
             * Hier wordt een bestaande invoer waarde gevalideerd.
             * Dit is nodig als een Tekstveld wordt omgezet naar een Datumveld.
             * @param {object} filter current filter object
             */
            $_valueInputSelected(event, filter) {
                filter.valueInput = event;
                this.$_validateFilterInputType(filter);
                if (filter.valueInput === VALUE_INPUT_TYPE.TEXTFIELD) {
                    // Reset all domain related properties
                    filter.domainId = null;
                    filter.domainType = null;
                    filter.value = '';
                }
                this.$_filterValidate(filter);
            },

            $_hasInvalidAdvancedStyle(style) {
                style.hasAdvancedStyleError =
                    style.customStylingOptions !== null &&
                    style.customStylingOptions.attribute === null;
                return style.hasAdvancedStyleError;
            },

            $_hasInvalidAdvancedStyleDomain(style) {
                style.hasAdvancedStyleErrorDomain =
                    style.customStylingOptions !== null &&
                    style.customStylingOptions.domainId === null &&
                    style.customStylingOptions.classificationType ===
                        STYLE_CLASSIFICATION_TYPES.NOMINAL;
                return style.hasAdvancedStyleErrorDomain;
            },

            $_onReevaluateStyle() {
                this.$_validateStyles();
            },

            $_setGeometryTypeOrder() {
                this.theme.themeLayers.forEach((themeLayer) => {
                    if (this.$_hasWfsServiceType(themeLayer)) {
                        const unOrderedGeometryType = themeLayer.geometryType;
                        let geometryType = '';
                        if (unOrderedGeometryType.indexOf('P') > -1) {
                            geometryType += 'P';
                        }
                        if (unOrderedGeometryType.indexOf('L') > -1) {
                            geometryType += 'L';
                        }
                        if (unOrderedGeometryType.indexOf('V') > -1) {
                            geometryType += 'V';
                        }
                        themeLayer.geometryType = geometryType;
                    }
                });
            },
            $_setMutateOptionsWfsT(themelayer, value) {
                // Currently only updating (U) is possible (new (N) and delete (R) may be possible in the future)
                themelayer.allowWfsTUpdate = value.indexOf('U') > -1;
            },
            $_getGeometryTypes() {
                for (const themeLayer of this.theme.themeLayers) {
                    // Split geometry types to array for radio group model binding
                    themeLayer.geometryTypeArray =
                        themeLayer.geometryType?.split('');
                }
            },
            $_getWfsTMutateOptions() {
                for (const themeLayer of this.theme.themeLayers) {
                    // Currently only updating (U) is possible (new (N) and delete (R) may be possible in the future)
                    if (themeLayer.isTransactional) {
                        themeLayer.mutateOptionsWfsTArray = [];
                        if (themeLayer.allowWfsTUpdate) {
                            themeLayer.mutateOptionsWfsTArray.push('U');
                        }
                    }
                }
            },
            $_setGeometryType(themelayer, value) {
                const geomTypeArray = value;
                // Join the geometry-types to string for storage in database
                // P for point, L for line, V for vlak (= polygon)
                themelayer.geometryType = geomTypeArray
                    ? geomTypeArray.join('')
                    : '';
            },

            $_hasPointGeometry(themelayer) {
                return themelayer.geometryType?.includes('P');
            },

            $_onOptionClick({ option, addTag }) {
                addTag(option);
                this.searchVal = '';
            },

            $_setFocusSearchLayer() {
                const inputField = this.$refs.searchLayer;
                if (inputField) {
                    inputField.$refs.input.focus();
                }
            },
            $_updateInformationAttributes(newVal, themelayer) {
                themelayer.informationAttributes = newVal;
            },
            $_activeInThemeLabel(active) {
                if (active) {
                    return this.$t('columnNameActive');
                } else {
                    return `${this.$t('columnNameActive')} ${this.$t(
                        'labelThemeLayerInactive'
                    )}`;
                }
            },
            $_notActiveLabel(active) {
                if (!active) {
                    return this.$t('themeLayerInActiveLabel');
                } else {
                    return this.$t('themeLayerInActiveInThemeLabel');
                }
            }
        }
    };
</script>

<style scoped>
.error {
    color: red;
}

.card-header .fa {
    transition: 0.3s transform ease-in-out;
}

.card-header .collapsed .fa {
    transform: rotate(90deg);
}

.fa-arrows-alt:hover {
    cursor: move;
}

.style-max-width {
    max-width: 570px;
}

/* Make sure there is enough space for colorpicker */
.extra-padding-bottom {
    padding-bottom: 175px;
}

.reduced-padding-top {
    padding-top: 0.25rem !important;
}

.checkbox-padding-bottom {
    padding-bottom: 0.25rem;
}

.themelayer-container {
    padding: 10px 0px;
    margin: 0px;
}

.style-label-md2 {
    width: 140px;
}

.style-label-md {
    width: 140px;
}
.transparency {
    padding-top: 8px;
}
.transparency-input {
    width: 80px;
}
</style>

<style>
.limited-heigt {
    overflow: auto;
    max-height: 300px;
    min-height: 100px;
}

li.fixed {
    position: sticky;
    top: 0;
    background-color: #fff;
    border-bottom: 1px solid rgb(233, 236, 239);
    padding-top: 0.5rem;
    padding-bottom: 0.4rem;
}
</style>
